import { FiFilm, FiGift, FiZap } from "react-icons/fi";
import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";
import ClipDetailPanel from "../../components/ClipDetailPanel/ClipDetailPanel";
import ScheduleModal from "../../components/ScheduleModal/ScheduleModal";
import JobProgressModal from "../../components/JobProgressModal/JobProgressModal";
import ClipsModal from "../../components/ClipsModal/ClipsModal";
import DeleteConfirmModal from "../../components/DeleteConfirmModal/DeleteConfirmModal";
import RussellOfferModal from "../../components/RussellOfferModal/RussellOfferModal";
import OwnWelcomeModal from "../../components/OwnWelcomeModal/OwnWelcomeModal";
import Onboarding, { TryFree } from "../../components/Onboarding/Onboarding";
import ClipLengthTrimmer from "../../components/ClipLengthTrimmer/ClipLengthTrimmer";
import ProfilePictureModal from "../../components/ProfilePictureModal/ProfilePictureModal";
import UpgradeModal from "../../components/UpgradeModal/UpgradeModal";
import HardPaywall from "../../components/HardPaywall/HardPaywall";
import { money } from "../../components/HardPaywall/HardPaywall";
import { formatLeft } from "../../components/HardPaywall/GiftOffer";
import { getRegion } from "../../utils/region";
import { track } from "../../utils/analytics";
import { trackTrialStartPixel } from "../../utils/adPixels";
import MomentCard from "../../components/MomentCard/MomentCard";
import ClipFeedViewer from "../../components/ClipFeedViewer/ClipFeedViewer";
import ClippingScanner from "../../components/ClippingScanner/ClippingScanner";
import LiveClippingModal from "../../components/LiveClippingModal/LiveClippingModal";
import { SiTwitch, SiKick, SiYoutube } from "react-icons/si";
import CoachingPanel from "../../components/MomentCard/CoachingPanel";
import { CalendarView } from "../calendar/CalendarPage";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import { SMS_CONSENT_LANGUAGE } from "../../utils/smsConsent";
import "./forYou.css";
import { jobDisplayTitle } from "../../utils/jobTitle";

// YouTube keeps an ENDED livestream at the same youtube.com/live/<id> URL, so a
// "live" YouTube link may really be a replay. Twitch/Kick channel URLs are not
// ambiguous that way.
const AMBIGUOUS_LIVE_URL = /(youtube\.com|youtu\.be)\//i;

// Demo mode (see below) is only offered to this account.
const DEMO_ADMIN_EMAIL = "clipbaitla@gmail.com";
const DEMO_KEY = "cb_demo_live";

function platformFromUrl(url = "") {
  if (/twitch\.tv/i.test(url)) return "twitch";
  if (/kick\.com/i.test(url)) return "kick";
  if (/youtube\.com|youtu\.be/i.test(url)) return "youtube";
  return "youtube";
}

function youtubeThumb(url = "") {
  const m = url.match(/(?:youtu\.be\/|v=|\/live\/|\/shorts\/)([\w-]{11})/);
  return m ? `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg` : null;
}

// Admin-only picker for demo mode: which finished clip run to replay as
// "live", and how many seconds between clips appearing.
function DemoPanel({ jobs, onStart, onCancel }) {
  const [jobId, setJobId] = useState(jobs[0]?._id || "");
  const [intervalSec, setIntervalSec] = useState(8);
  return (
    <div className="foryou-demo-panel">
      <div className="foryou-demo-title"><FiFilm /> Demo mode — replay a clip run as live</div>
      {jobs.length === 0 ? (
        <div className="foryou-demo-hint">
          No finished clip runs yet. Paste the stream link on Home first, let it finish, then come back.
        </div>
      ) : (
        <>
          <label className="foryou-demo-label">
            Clip run
            <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
              {jobs.map((j) => (
                <option key={j._id} value={j._id}>
                  {jobDisplayTitle(j).slice(0, 70)}{j.streamerName ? ` · ${j.streamerName}` : ""} · {j.clips.length} clips
                </option>
              ))}
            </select>
          </label>
          <label className="foryou-demo-label">
            Seconds between clips
            <input
              type="number"
              min={2}
              max={120}
              value={intervalSec}
              onChange={(e) => setIntervalSec(Math.max(2, Math.min(120, Number(e.target.value) || 8)))}
            />
          </label>
          <div className="foryou-demo-hint">Only you see this. Nothing is saved. Press Shift+D to stop.</div>
        </>
      )}
      <div className="foryou-demo-actions">
        <button type="button" className="foryou-demo-cancel" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="foryou-demo-start"
          disabled={!jobId}
          onClick={() => onStart(jobId, intervalSec)}
        >
          Start demo
        </button>
      </div>
    </div>
  );
}

const CONTENT_TYPES = [
  { value: "gaming",       labelKey: "contentTypes.gaming" },
  { value: "talking_head", labelKey: "contentTypes.podcastTalkingHead" },
  { value: "irl",          labelKey: "contentTypes.irlVlog" },
  { value: "music",        labelKey: "contentTypes.music" },
  { value: "documentary",  labelKey: "contentTypes.documentary" },
];

// ClipFinder (coach mode) is hidden — Clipper ("streamer") is the silent
// default and only mode. Coach generation/panel stay in the codebase, unsurfaced.

// True while the user is partway through onboarding (it saves progress in
// localStorage, including before a Twitch/Kick/YouTube connect redirect).
function onboardingInProgress() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return !!localStorage.getItem(`onboarding_progress_${u?._id || "anon"}`);
  } catch { return false; }
}

const ForYouPage = () => {
  const { t } = useTranslation('clips');
  const { t: tp } = useTranslation('paywall');
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "library" ? "library" : "home"
  );

  // Sync tab from URL (bottom nav navigates to /home?tab=library)
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    setActiveTab(tab === "library" ? "library" : "home");
  }, [location.search]);

  // Any navigation (bottom nav) closes the full-screen viewer and minimizes the
  // clipping scanner (the job keeps running; a pill lets you re-open it).
  useEffect(() => {
    setViewerStart(null);
    setPublishTarget(null);
    setScannerOpen(false);
  }, [location.pathname, location.search]);

  // Feedback when returning from a channel OAuth redirect (?twitch= / ?kick=).
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    for (const [key, name] of [["twitch", "Twitch"], ["kick", "Kick"], ["youtube", "YouTube"]]) {
      const t = sp.get(key);
      if (!t) continue;
      // Mid-onboarding the onboarding screen already shows it's connected.
      if (t === "connected") { if (!onboardingInProgress()) toast.success(`${name} connected — we'll auto-clip every stream.`); }
      else if (t === "denied") toast(`${name} connection cancelled.`);
      else if (t === "switch") toast.error(`Disconnect your current channel before connecting ${name}.`, { duration: 4000 });
      else toast.error(`Couldn't connect ${name}. Try again.`);
      sp.delete(key);
    }
    const next = sp.toString();
    window.history.replaceState({}, "", next ? `${location.pathname}?${next}` : location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hard-lock all scrolling on the Home tab (no Y-axis scroll). Locks body +
  // html + the page scroller directly so nothing can scroll; restored on
  // Library/leaving so the grid still scrolls.
  useEffect(() => {
    if (activeTab !== "home") return;
    const targets = [document.documentElement, document.body];
    const prev = targets.map((el) => el.style.overflow);
    targets.forEach((el) => { el.style.overflow = "hidden"; });
    return () => { targets.forEach((el, i) => { el.style.overflow = prev[i]; }); };
  }, [activeTab]);

  // "Saved!" toast when returning from /editor/ after a successful
  // export. The editor's Clipbait export bridge redirects with
  // ?editedClip=1 — we fire the toast once and strip the param so
  // refreshes don't re-trigger it.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("editedClip") === "1") {
      toast.success("Saved! Your edited clip is ready.");
      sp.delete("editedClip");
      const next = sp.toString();
      window.history.replaceState({}, "", next ? `${location.pathname}?${next}` : location.pathname);
    }
    // run once on mount — location is the imported react-router location
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "calendar") {
      window.dispatchEvent(new Event("refetch_calendar"));
    }
  }, [activeTab]);

  // Profile picture modal. Adding a photo is OPTIONAL — we no longer force it
  // on landing (it annoyed users). It's reachable from /me and the profile
  // dropdown ("Update profile picture"). Keep the state for those entry points.
  const [showProfilePicModal, setShowProfilePicModal] = useState(false);
  useEffect(() => {
    if (!currentUser?._id) return;
    axiosInstance.get(`/users?userId=${currentUser._id}`)
      .then((res) => {
        // Intentionally no auto-prompt. Capture OWN bonus status for the banner.
        const s = res?.data?.referralBonusStatus;
        if (s) setReferralBonusStatus(s);
      })
      .catch(() => {});
  }, [currentUser?._id]);

  // Onboarding state
  // Mid-onboarding (e.g. back from a Twitch connect) → show it on the first
  // frame instead of flashing the home page while the server check runs.
  const [showOnboarding, setShowOnboarding] = useState(() => onboardingInProgress());
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Referral offer state
  const [showRussellOffer, setShowRussellOffer] = useState(false);
  const [ownWelcome, setOwnWelcome] = useState({ open: false });
  const [ownWelcomeShown, setOwnWelcomeShown] = useState(false);
  const [referralSource, setReferralSource] = useState("");
  // OWN partner bonus state: none | pending | granted | revoked. "pending"
  // shows the "150 credits unlock on first paid plan" banner.
  const [referralBonusStatus, setReferralBonusStatus] = useState("none");

  // Plan state
  const [userPlan, setUserPlan] = useState(null); // null = loading, "none" | "Starter" etc.
  const [userPlanInterval, setUserPlanInterval] = useState(null); // "monthly" | "annual" | null
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [creatorSettingsLoaded, setCreatorSettingsLoaded] = useState(false);
  // "You're on the Free Plan" banner — closable; remembered in this browser.
  const FREE_BANNER_KEY = "cb_free_banner_dismissed";
  const [freeBannerDismissed, setFreeBannerDismissed] = useState(() => {
    try { return localStorage.getItem(FREE_BANNER_KEY) === "1"; } catch { return false; }
  });
  // The Free Plan banner is desktop-only; on phones it crowds the top bar.
  // 900px = the app's mobile breakpoint (where the bottom tab bar appears).
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia("(max-width: 900px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsNarrow(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  const dismissFreeBanner = () => {
    setFreeBannerDismissed(true);
    try { localStorage.setItem(FREE_BANNER_KEY, "1"); } catch { /* private mode etc. */ }
  };
  // Just back from the hard paywall's Stripe checkout — wait for activation.
  const [awaitingPayment, setAwaitingPayment] = useState(
    () => new URLSearchParams(window.location.search).get("paywall") === "paid"
  );
  const [viewerStart, setViewerStart] = useState(null); // index into allClips for the FYP clip viewer (null = closed)
  const [publishTarget, setPublishTarget] = useState(null); // {clip, job, clipIndex} for the publish/schedule popup
  const urlInputRef = useRef(null); // focus target for the home hero "Paste link" button
  const [scannerJob, setScannerJob] = useState(null); // {jobId, brandImage, title} being tracked (null = no active clipping run)
  const [scannerOpen, setScannerOpen] = useState(false); // full-screen scanner showing vs minimized (job keeps running)
  const [newClips, setNewClips] = useState(0); // unseen clips from the current run (Library tab badge)
  const [liveScannerOpen, setLiveScannerOpen] = useState(false); // Sherlock scanner for an auto/live session
  const [liveDismissed, setLiveDismissed] = useState(false); // user minimized the live scanner
  const [composerOpen, setComposerOpen] = useState(false); // Sherlock-style: the "Paste A Link" button morphs into an inline input
  const [pendingUpload, setPendingUpload] = useState(false); // fire handleUpload once a picked file lands in state
  const homeFileInputRef = useRef(null);
  const heroInputRef = useRef(null); // the inline input pill — tap outside to collapse
  const [twitchConn, setTwitchConn] = useState(null); // {connected, username, avatar} for the connected-channel state
  const [kickConn, setKickConn] = useState(null); // same, for a connected Kick channel
  const [youtubeConn, setYoutubeConn] = useState(null); // same, for a connected YouTube channel (auto-clip)
  // Opens the pricing as an in-app POPUP (standard SaaS funnel) rather than
  // navigating away. Russell-referral users still get their special offer.
  // Paying users (Pro / Lifetime) who hit an upgrade prompt are almost always
  // out of credits — open straight on credit packs, not plans they already have.
  const [upgradeTab, setUpgradeTab] = useState("plans");
  const openUpgrade = (tab) => {
    const paid = !!userPlan && userPlan !== "none";
    if (referralSource === "russell" && !paid) {
      setShowRussellOffer(true);
    } else if (!paid && tab !== "credits") {
      // Upgrade / Unlock live clipping → the hard paywall, not the old plans modal.
      track("paywall_opened", { from: "upgrade" });
      setForcePaywall(true);
    } else {
      setUpgradeTab(typeof tab === "string" ? tab : paid ? "credits" : "plans");
      setShowUpgradeModal(true);
    }
  };

  // Closing the plans popup. For an OWN pending user who exits WITHOUT
  // subscribing, surface the welcome popup once to inspire them to come back.
  const handleUpgradeClose = () => {
    setShowUpgradeModal(false);
    if (referralBonusStatus === "pending" && !ownWelcomeShown) {
      setOwnWelcomeShown(true);
      setOwnWelcome({ open: true });
    }
  };

  // Credits-empty popup
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  // Out of credits. A $1-trial account goes straight to the trial plan picker
  // (UpgradeModal → TrialPlanPicker) to choose how many credits it wants;
  // everyone else gets the small top-up popup.
  const showOutOfCredits = () => {
    axiosInstance.get("/trial/status")
      .then((r) => { if (r.data?.trialing) openUpgrade("plans"); else setShowCreditsPopup(true); })
      .catch(() => setShowCreditsPopup(true));
  };
  const autoOutOfCreditsShown = useRef(false);

  // Form state
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  // User's preset creator settings from /me page
  const [userPlatform, setUserPlatform] = useState(null);
  const [userChannelUsername, setUserChannelUsername] = useState("");
  const [brandProfilePicture, setBrandProfilePicture] = useState(null);
  const [activeBrandId, setActiveBrandId] = useState(null);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsLoadedOnce, setJobsLoadedOnce] = useState(false);

  // YouTube-only: fetched via oEmbed (keep existing)
  const [videoTitles, setVideoTitles] = useState({});
  const [videoThumbs, setVideoThumbs] = useState({});

  // Modal state
  // Live clipping — popup triggered when a live Twitch link is pasted (Pro feature).
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const [liveModalUrl, setLiveModalUrl] = useState("");
  const [activeLiveSession, setActiveLiveSession] = useState(null);
  const [endedLiveSession, setEndedLiveSession] = useState(null); // just-ended session shown in a "done" state (out-of-credits / stream-ended) until dismissed
  const stoppedLiveIdRef = useRef(null); // id we just stopped — don't let a poll re-add it
  const prevActiveLiveIdRef = useRef(null); // id of the session we were tracking, to catch its end
  const dismissedEndedIdRef = useRef(null); // ended session the user has dismissed — don't resurface
  const [liveSessions, setLiveSessions] = useState([]);

  // ── Demo mode (admin content recording) ──
  // Replays a finished clip run in the LIVE UI — the live card, the live
  // scanner and clips popping into the Library one by one — so the live
  // experience can be screen-recorded on any stream. Purely visual: nothing
  // is sent to the server, and it only exists for the admin account.
  const isDemoAdmin = (currentUser?.email || "").toLowerCase() === DEMO_ADMIN_EMAIL;
  const [demoLive, setDemoLive] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(DEMO_KEY)) || null; } catch { return null; }
  }); // { jobId, revealed, intervalSec } | null
  const [demoPanelOpen, setDemoPanelOpen] = useState(false);
  useEffect(() => {
    try {
      if (demoLive) sessionStorage.setItem(DEMO_KEY, JSON.stringify(demoLive));
      else sessionStorage.removeItem(DEMO_KEY);
    } catch { /* ignore */ }
  }, [demoLive]);
  const demoJob = isDemoAdmin && demoLive ? jobs.find((j) => String(j._id) === String(demoLive.jobId)) : null;
  // Reveal the next clip every intervalSec, like clips landing mid-stream.
  useEffect(() => {
    if (!demoJob || !demoLive) return undefined;
    if (demoLive.revealed >= (demoJob.clips?.length || 0)) return undefined;
    const t = setTimeout(
      () => setDemoLive((d) => (d ? { ...d, revealed: d.revealed + 1 } : d)),
      Math.max(2, demoLive.intervalSec || 8) * 1000
    );
    return () => clearTimeout(t);
  }, [demoJob, demoLive]);
  const stopDemo = () => { setDemoLive(null); setDemoPanelOpen(false); };
  // Shift+D stops the demo without any on-screen control (keeps recordings clean).
  useEffect(() => {
    if (!demoLive) return undefined;
    const onKey = (e) => { if (e.shiftKey && (e.key === "D" || e.key === "d")) stopDemo(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [demoLive]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showClipsModal, setShowClipsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  const [limitResetDate, setLimitResetDate] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [clipToDelete, setClipToDelete] = useState(null); // { clip, job, clipIndex }
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState(null); // [{ job, clipIndex }]

  // Content type selection
  // Default to "talking_head" — most common niche, and the only one
  // a mobile user sees by default (the niche dropup is hidden on
  // mobile to declutter the bar). Desktop users can still change it.
  const [contentType, setContentType] = useState("talking_head");

  // Generation options
  const [aspectRatio, setAspectRatio] = useState("9:16");
  // Clip count. Default "auto" = let the AI decide how many good moments
  // exist (sends the worker's standard target of 9, which it treats as an
  // upper bound and fills down to ~4 quality clips). Picking a number caps
  // the job to exactly that many. Chosen via the dropup selector below.
  // The "how many clips" picker is hidden for now (it doesn't work yet), so
  // every job stays on auto. Flip SHOW_CLIP_COUNT to bring it back.
  const SHOW_CLIP_COUNT = false;
  const [clipCount, setClipCount] = useState("auto");
  const [clipCountOpen, setClipCountOpen] = useState(false);
  // Auto = up to the worker's ceiling (20): selection ships only clips that pass
  // its blind review, so a big ceiling means good extras aren't dropped, never
  // filler. Credits are per minute of video, not per clip.
  const maxClips = clipCount === "auto" ? 20 : clipCount;
  // Generation mode — "streamer" renders clips (existing flow), "coach"
  // returns moments + editing guidance + music recs for ~60× less credits.
  // Solves the third-world clipper accessibility problem.
  const [genMode, setGenMode] = useState("streamer");
  // Coach mode optional render aspect: "" | "9:16" | "16:9"
  //   ""        = guidance only (1 credit flat)
  //   "9:16"    = guidance + render each moment vertical (1 + N credits)
  //   "16:9"    = guidance + render each moment widescreen (1 + N credits)
  // Burn-in toggles — sent to the worker so it can skip the matching
  // FFmpeg overlay when the user opts out. Only meaningful on a render
  // path. Clip rendering is now reserved for Clipper mode only;
  // ClipFinder is guidance-only, so the burn toggles never show in
  // ClipFinder regardless of aspect.
  // Channel watermark defaults ON for both 9:16 and 16:9 — the platform logo +
  // @handle brand the clip out of the box; users can still toggle it off.
  // Title / subtitles also default ON since those help retention on 9:16.
  const [burnHookTitle, setBurnHookTitle] = useState(true);
  const [burnSubtitles, setBurnSubtitles] = useState(true);
  const [burnWatermark, setBurnWatermark] = useState(true);
  const showBurnToggles = genMode === "streamer";
  // Clipper 16:9 = landscape clip the user is editing downstream.
  // Title captions + subtitles don't apply (they'd fight the editor's
  // own work), so we hide both toggles and force them off in the
  // payload regardless of last toggle state. Watermark stays available.
  const titleAndSubsAvailable = !(genMode === "streamer" && aspectRatio === "16:9");
  const effectiveBurnHookTitle = burnHookTitle && titleAndSubsAvailable;
  const effectiveBurnSubtitles = burnSubtitles && titleAndSubsAvailable;

  // Pre-generation duration detection — lets us show an exact credit
  // estimate before the user spends anything.
  // detectedDuration is in SECONDS. null = unknown.
  const [detectedDuration, setDetectedDuration] = useState(null);
  // { url, message } when the pasted link can't be clipped (e.g. a
  // sub-only Twitch VOD) — blocks Generate before the paywall/charge.
  const [urlProbeError, setUrlProbeError] = useState(null);
  const [detectedDurationLoading, setDetectedDurationLoading] = useState(false);

  // Source clip range (seconds). null = full video. Reset to full whenever a
  // new duration is detected so the selector always starts at "whole video".
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  useEffect(() => {
    if (Number.isFinite(detectedDuration) && detectedDuration > 0) {
      setRangeStart(0);
      setRangeEnd(detectedDuration);
    } else {
      setRangeStart(null);
      setRangeEnd(null);
    }
  }, [detectedDuration]);

  // Credit balance
  const [generationsRemaining, setGenerationsRemaining] = useState(null);
  const [generationsTotal, setGenerationsTotal] = useState(null);
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  // Next-to-expire credit bucket from the new Opus-style FIFO model
  const [nextExpiringGrant, setNextExpiringGrant] = useState(null);
  const [activeGrants, setActiveGrants] = useState([]);

  // Phone-number prompt for SMS clip-ready notifications
  const [userPhoneNumber, setUserPhoneNumber] = useState(null);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phonePromptSkipped, setPhonePromptSkipped] = useState(false);
  // Explicit SMS consent — TCPA / A2P 10DLC requires this be UNCHECKED by
  // default, separate from any other consent (ToS, privacy, etc.), and
  // optional (user can dismiss the modal without checking it).
  const [smsConsent, setSmsConsent] = useState(false);
  // Holds the pending generate fn while the modal is open
  const [pendingGenerateFn, setPendingGenerateFn] = useState(null);

  // Show the out-of-credits prompt when a paid user hits 0 credits — once per
  // visit, since for trial users it's the full-screen plan picker.
  useEffect(() => {
    if (generationsRemaining === 0 && userPlan && userPlan !== "none" && !autoOutOfCreditsShown.current) {
      autoOutOfCreditsShown.current = true;
      showOutOfCredits();
    }
  }, [generationsRemaining, userPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clip selection (for bulk download)
  const [selectedClipIds, setSelectedClipIds] = useState([]);
  // Liked clips (persisted to localStorage)
  const [likedClipIds, setLikedClipIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('clipbait_liked') || '[]'); }
    catch { return []; }
  });

  // Single-clip detail panel (Higgsfield-style)
  const [selectedClipDetail, setSelectedClipDetail] = useState(null); // { clip, job, clipIndex }

  // Streak tracking
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    const count = parseInt(localStorage.getItem('clipbait_streak_count') || '0');
    const lastDate = localStorage.getItem('clipbait_streak_last');
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (lastDate === today || lastDate === yesterday) setStreak(count);
  }, []);

  const updateStreak = () => {
    const today = new Date().toDateString();
    const lastDate = localStorage.getItem('clipbait_streak_last');
    const count = parseInt(localStorage.getItem('clipbait_streak_count') || '0');
    if (lastDate === today) return;
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const newCount = lastDate === yesterday ? count + 1 : 1;
    localStorage.setItem('clipbait_streak_last', today);
    localStorage.setItem('clipbait_streak_count', String(newCount));
    setStreak(newCount);
  };

  // Input mode: URL paste or file upload
  const [inputMode, setInputMode] = useState("url");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);

  // File upload → detect duration client-side via HTMLVideoElement.
  // Instant + accurate, no backend round-trip.
  useEffect(() => {
    if (!selectedFile) { setDetectedDuration(null); return; }
    setDetectedDurationLoading(true);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    const objectUrl = URL.createObjectURL(selectedFile);
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration)) setDetectedDuration(v.duration);
      setDetectedDurationLoading(false);
      URL.revokeObjectURL(objectUrl);
    };
    v.onerror = () => {
      setDetectedDuration(null);
      setDetectedDurationLoading(false);
      URL.revokeObjectURL(objectUrl);
    };
    v.src = objectUrl;
    return () => { try { URL.revokeObjectURL(objectUrl); } catch (_) {} };
  }, [selectedFile]);

  // URL paste → debounced probe via /api/clips/probe-url. Returns null
  // for non-YouTube platforms (we just don't show the estimate).
  useEffect(() => {
    if (inputMode !== "url") return;
    if (!videoUrl || !/^https?:\/\//i.test(videoUrl)) {
      setDetectedDuration(null);
      setUrlProbeError(null);
      setDetectedDurationLoading(false);
      return;
    }
    setDetectedDurationLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axiosInstance.post("/clips/probe-url", { url: videoUrl });
        setDetectedDuration(res.data?.durationSeconds ?? null);
        if (res.data?.error && res.data?.message) {
          setUrlProbeError({ url: videoUrl, message: res.data.message });
          toast.error(res.data.message, { id: "url-probe-error", duration: 8000 });
        } else {
          setUrlProbeError(null);
        }
      } catch (_) {
        setDetectedDuration(null);
      } finally {
        setDetectedDurationLoading(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [videoUrl, inputMode]);

  // Animated placeholder
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const placeholders = [
    t('pasteYouTubeLivestream'),
    t('pasteRumbleLivestream'),
    t('pasteGanjingWorldLink'),
    t('pasteKickLink'),
    t('pasteTwitchLink'),
    t('pasteXLink'),
  ];


  // Fetch video info - YouTube only (keep existing)
  const fetchVideoInfo = useCallback(async (url, jobId) => {
    if (!url || videoTitles[jobId] !== undefined) return;

    // Only fetch for YouTube - Rumble uses worker metadata
    if (!url.includes("youtube.com") && !url.includes("youtu.be")) {
      return;
    }

    try {
      // YouTube - use oEmbed API
      const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
      const res = await fetch(oembedUrl);
      if (res.ok) {
        const data = await res.json();
        if (data.title) {
          setVideoTitles(prev => ({ ...prev, [jobId]: data.title }));
          // Extract thumbnail from YouTube URL
          const videoId = url.match(/(?:v=|youtu\.be\/)([^&?]+)/)?.[1];
          if (videoId) {
            setVideoThumbs(prev => ({ ...prev, [jobId]: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` }));
          }
          return;
        }
      }

      // Fallback to noembed
      const noembedRes = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
      const noembedData = await noembedRes.json();
      setVideoTitles(prev => ({ ...prev, [jobId]: noembedData.title || null }));
      if (noembedData.thumbnail_url) {
        setVideoThumbs(prev => ({ ...prev, [jobId]: noembedData.thumbnail_url }));
      }
    } catch (err) {
      console.log('Could not fetch video info:', err);
      setVideoTitles(prev => ({ ...prev, [jobId]: null }));
    }
  }, [videoTitles]);

  // Clean up query params after redirects (OAuth, Stripe checkout)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connected")) {
      toast.success(t('common:connectedTo', { platform: params.get("connected") }));
      navigate("/", { replace: true });
    }
    if (params.get("plan_success")) {
      toast.success(t('common:planActivated'));
      setUserPlan("Starter");
      navigate("/", { replace: true });
    }
  }, [location.search, navigate]);

  // Check onboarding status on mount
  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const res = await axiosInstance.get("/users/me/onboarding");
        setShowOnboarding(!res.data.completed);
      } catch (err) {
        // If error, don't show onboarding (fail gracefully)
      } finally {
        setOnboardingChecked(true);
      }
    };
    checkOnboarding();
  }, []);

  // Surface a running live-clipping session on the home page so a Pro user can
  // reopen it (clips keep flowing server-side even with the popup closed).
  const refreshActiveLive = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/live");
      const list = res.data?.sessions || [];
      setLiveSessions(list);
      const live = list.find(
        (s) => (s.status === "active" || s.status === "starting") && String(s._id) !== stoppedLiveIdRef.current
      );
      if (live) {
        prevActiveLiveIdRef.current = String(live._id);
        setActiveLiveSession(live);
        setEndedLiveSession(null); // an active session takes precedence over any ended one
        return;
      }
      setActiveLiveSession(null);
      // The session we were tracking just ended — surface it once in a "done"
      // state so its clips + reason (e.g. out of credits) don't silently vanish.
      // Skip user-stopped sessions (they chose to stop) and already-dismissed ones.
      const prevId = prevActiveLiveIdRef.current;
      if (prevId && dismissedEndedIdRef.current !== prevId) {
        const ended = list.find((s) => String(s._id) === prevId && s.status === "ended");
        const show = ended && (ended.endedReason === "credits" || (ended.endedReason === "stream-ended" && (ended.clips?.length || 0) > 0));
        setEndedLiveSession(show ? ended : null);
      }
    } catch { /* best-effort */ }
  }, []);
  useEffect(() => { refreshActiveLive(); }, [refreshActiveLive]);
  // Poll so an AUTO go-live (Twitch EventSub) is picked up within ~20s. Pause
  // while the tab is hidden — no reason to hammer the DB from backgrounded tabs,
  // and refresh once on return so state is current.
  useEffect(() => {
    let id = null;
    const start = () => { if (!id) id = setInterval(refreshActiveLive, 20000); };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (document.visibilityState === "visible") { refreshActiveLive(); start(); }
      else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [refreshActiveLive]);

  // Auto-open the Sherlock scanner (live mode) whenever a session is running,
  // unless the user minimized it. Reset when the session ends.
  useEffect(() => {
    const active = activeLiveSession && (activeLiveSession.status === "active" || activeLiveSession.status === "starting");
    // Demo mode counts as a running session for the scanner.
    if ((active || (isDemoAdmin && demoLive)) && !liveDismissed) setLiveScannerOpen(true);
    if (!activeLiveSession && !(isDemoAdmin && demoLive)) { setLiveScannerOpen(false); setLiveDismissed(false); }
  }, [activeLiveSession, liveDismissed, demoLive, isDemoAdmin]);

  // Fetch user's plan status on mount
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await axiosInstance.get("/billing/plan/status");
        setUserPlan(res.data?.plan || "none");
        setUserPlanInterval(res.data?.interval || null);
        if (typeof res.data?.generationsRemaining === "number") {
          setGenerationsRemaining(res.data.generationsRemaining);
        }
        if (typeof res.data?.creditsPeriodTotal === "number") {
          setGenerationsTotal(res.data.creditsPeriodTotal);
        }
        if (typeof res.data?.credits === "number") {
          setPurchasedCredits(res.data.credits);
        }
        if (res.data?.nextExpiringGrant !== undefined) {
          setNextExpiringGrant(res.data.nextExpiringGrant);
        }
        if (Array.isArray(res.data?.activeGrants)) {
          setActiveGrants(res.data.activeGrants);
        }
        if (res.data?.phoneNumber !== undefined) {
          setUserPhoneNumber(res.data.phoneNumber);
        }
      } catch (_) {
        setUserPlan("none");
      }
    };
    fetchPlan();
  }, []);

  // NOTE: We intentionally do NOT redirect new/unpaid users to the upgrade
  // page on landing. Standard SaaS funnel: let them browse the app freely,
  // then show the pricing modal only when they hit a gated action (Generate /
  // Upload). After they pay, onboarding shows (it's gated on userPlan !==
  // "none" in the render below). See handleGenerate/handleUpload paywall gate.

  // The pricing popup is shown right after onboarding completes (see the
  // Onboarding onComplete handler below) and on any gated action — not auto on
  // every landing.

  // Wraps an async generator with a phone-prompt check. If the user has no
  // phone and hasn't skipped the prompt this session, opens the modal and
  // defers the generation until they save or skip.
  const withPhoneCheck = async (genFn) => {
    if (!userPhoneNumber && !phonePromptSkipped) {
      setPendingGenerateFn(() => genFn);
      setShowPhonePrompt(true);
      return;
    }
    await genFn();
  };

  const savePhoneNumber = async () => {
    if (!phoneInput.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    if (!smsConsent) {
      toast.error("Please check the consent box to receive texts");
      return;
    }
    setPhoneSaving(true);
    try {
      const res = await axiosInstance.post("/sms/phone", {
        phoneNumber: phoneInput,
        smsConsent: true,
        consentLanguage: SMS_CONSENT_LANGUAGE,
      });
      setUserPhoneNumber(res.data.phoneNumber);
      toast.success("Phone saved — we'll text you when clips are ready");
      setShowPhonePrompt(false);
      setPhoneInput("");
      setSmsConsent(false);
      const pending = pendingGenerateFn;
      setPendingGenerateFn(null);
      if (pending) await pending();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't save phone");
    } finally {
      setPhoneSaving(false);
    }
  };

  const skipPhonePrompt = async () => {
    setPhonePromptSkipped(true);
    setShowPhonePrompt(false);
    setPhoneInput("");
    setSmsConsent(false);
    const pending = pendingGenerateFn;
    setPendingGenerateFn(null);
    if (pending) await pending();
  };

  // Fetch user's creator settings on mount
  useEffect(() => {
    const fetchCreatorSettings = async () => {
      try {
        const res = await axiosInstance.get("/users/me/creator-settings");
        if (res.data?.creatorSettings) {
          setUserPlatform(res.data.creatorSettings.platform || null);
          setUserChannelUsername(res.data.creatorSettings.channelUsername || "");
          if (res.data.creatorSettings.streamType) {
            setContentType(res.data.creatorSettings.streamType);
          }
        }
        if (res.data?.activeBrandId) {
          setActiveBrandId(res.data.activeBrandId);
        }
        if (res.data?.profilePicture) {
          setBrandProfilePicture(res.data.profilePicture);
        }
        setTwitchConn(res.data?.socialConnections?.twitch?.connected ? res.data.socialConnections.twitch : null);
        setKickConn(res.data?.socialConnections?.kick?.connected ? res.data.socialConnections.kick : null);
        const yt = res.data?.socialConnections?.youtube;
        setYoutubeConn(yt?.connected && yt?.autoClip ? yt : null);
        if (res.data?.referralSource) {
          setReferralSource(res.data.referralSource);
        }
      } catch (err) {
        // Silently fail - user might not have settings
      } finally {
        setCreatorSettingsLoaded(true);
      }
    };
    fetchCreatorSettings();
  }, [showOnboarding]); // Refetch after onboarding completes

  // "Clip this with AI" from a campaign brief lands here as
  // /home?clip=<url>&campaign=<id>: start a normal run on that source once the
  // plan and creator settings are loaded (handleGenerate applies the paywall
  // and every usual check). The campaign id goes along so the server can pass
  // that campaign's guidance to the worker.
  const campaignRunRef = useRef(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const clip = sp.get("clip");
    if (!clip) return;
    campaignRunRef.current = { url: clip, campaignId: sp.get("campaign") || null, started: false };
    setVideoUrl(clip);
    sp.delete("clip");
    sp.delete("campaign");
    const next = sp.toString();
    window.history.replaceState({}, "", next ? `${window.location.pathname}?${next}` : window.location.pathname);
  }, []);
  useEffect(() => {
    const run = campaignRunRef.current;
    if (!run || run.started || userPlan === null || !creatorSettingsLoaded) return;
    run.started = true;
    handleGenerate(run.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlan, creatorSettingsLoaded]);

  // Hard paywall: signup → onboarding → pay. They can close it to look around,
  // but ANY click brings it straight back — except the profile menu toggle and
  // Sign out (marked data-paywall-allow), so people can always log out.
  // userPlan === null = still loading.
  const [paywallDismissed, setPaywallDismissed] = useState(false);
  const [forcePaywall, setForcePaywall] = useState(false); // opened from Upgrade / Unlock live clipping
  // "Try it free" run (routes/freeRun.js), one smooth flow after onboarding:
  //   clipping scanner (+ text-me prompt) → "View N clips" → the clips play
  //   (locked) → paywall "Your N clips are ready" → leaving lands on the
  //   Library where clips play but download/edit/post reopen the paywall.
  const [freeRunPendingId, setFreeRunPendingId] = useState(null); // from onboarding, before jobs reload
  const [freeRunReveal, setFreeRunReveal] = useState(false); // viewer open on the free clips
  const [freeRunVideoUrl, setFreeRunVideoUrl] = useState(null);
  // A just-started run (pending id) is THE run, even before it shows up in the
  // job list. Never fall back to an older (e.g. failed) try run in that case.
  const freeRunJob = freeRunPendingId
    ? jobs.find((j) => j._id === freeRunPendingId) || null
    : jobs.filter((j) => j.freeRun).sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt))[0] || null;
  // The free run's id even before the job list has reloaded after onboarding.
  const freeRunId = freeRunPendingId || freeRunJob?._id || null;
  const [freeRunRetry, setFreeRunRetry] = useState(false); // picking another stream after a failed run
  const freeRunRunning = freeRunJob
    ? freeRunJob.status === "queued" || freeRunJob.status === "processing"
    : !!freeRunPendingId;
  const freeRunClips = freeRunJob?.status === "completed" ? (freeRunJob.clips || []).filter((c) => c.url) : [];
  const freeRunClipsSoFar = (freeRunJob?.clips || []).filter((c) => c.url);
  const revealKey = freeRunJob ? `free_run_revealed_${freeRunJob._id}` : null;
  const [revealTick, setRevealTick] = useState(0);
  const freeRunRevealed = (() => {
    void revealTick;
    try { return !revealKey || localStorage.getItem(revealKey) === "1"; } catch { return true; }
  })();
  const freeRunAwaitingReveal = freeRunClips.length > 0 && !freeRunRevealed;
  const markFreeRunRevealed = () => {
    try { if (revealKey) localStorage.setItem(revealKey, "1"); } catch { /* ignore */ }
    setRevealTick((n) => n + 1);
  };
  const freeRunInScanner = !!freeRunId && scannerOpen && scannerJob?.jobId === freeRunId;
  // Unpaid free-run clips stay locked behind the paywall; once they've paid
  // (the $1 trial now starts the run) it's an ordinary job.
  const freeRunLocked = userPlan === "none";
  const needsPaywall =
    jobsLoadedOnce && // a try run in progress must be known before deciding
    onboardingChecked &&
    !showOnboarding &&
    !freeRunRunning &&
    !freeRunInScanner &&
    !freeRunAwaitingReveal &&
    !freeRunReveal &&
    creatorSettingsLoaded &&
    userPlan === "none" &&
    !(purchasedCredits > 0) &&
    referralSource !== "russell" &&
    referralBonusStatus !== "pending";
  // Closed the paywall without paying: home shows locked versions of the actions.
  const homeLocked = needsPaywall && paywallDismissed;
  // Locked actions (download, post, bulk download) reopen the paywall instead.
  const requirePro = (fn) => (...args) => {
    if (homeLocked) {
      args.find((x) => x && typeof x.stopPropagation === "function")?.stopPropagation();
      track("paywall_reopened", { from: "locked_action" });
      setViewerStart(null);
      setPaywallDismissed(false);
      return undefined;
    }
    return fn(...args);
  };
  // After onboarding (or back from the "clips ready" text/email): the free
  // run's scanner, until they've seen the clips.
  useEffect(() => {
    if (showOnboarding || scannerJob || freeRunReveal) return;
    if (!freeRunRunning && !freeRunAwaitingReveal) return;
    const id = freeRunId;
    if (!id) return;
    setScannerJob({ jobId: id, brandImage: brandProfilePicture, title: (freeRunJob && getVideoTitle(freeRunJob)) || freeRunJob?.streamerName || "Your stream", videoUrl: freeRunJob?.videoUrl || freeRunVideoUrl });
    setScannerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding, freeRunRunning, freeRunAwaitingReveal, freeRunJob?._id, freeRunPendingId, freeRunReveal, scannerJob]);
  // "Text me when it's ready" — once per run, and only after their saved phone
  // number has loaded (it comes with the plan status), so a refresh mid-run
  // doesn't ask again.
  useEffect(() => {
    if (showOnboarding || !freeRunRunning || !freeRunId || userPlan === null) return;
    if (userPhoneNumber || phonePromptSkipped) return;
    const key = `free_run_phone_asked_${freeRunId}`;
    try { if (localStorage.getItem(key)) return; localStorage.setItem(key, "1"); } catch { /* ignore */ }
    setPendingGenerateFn(() => async () => {});
    setShowPhonePrompt(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding, freeRunRunning, freeRunId, userPlan, userPhoneNumber]);
  // The reveal: the free clips in the full-screen player, locked. Closing it or
  // tapping a lock brings up the paywall.
  // "View clips" → straight to the paywall: their real thumbnails, locked.
  // Nothing plays until they unlock (giving the content away lowers conversion).
  const openFreeRunReveal = () => {
    track("free_run_clips_viewed", { clips: freeRunClipsSoFar.length, done: !freeRunRunning });
    if (freeRunRunning) {
      // First clips are in while the rest render: paywall now; closing it
      // returns to the progress screen.
      setScannerOpen(false);
      setScannerJob(null);
      setForcePaywall(true);
      return;
    }
    markFreeRunRevealed();
    setScannerOpen(false);
    setScannerJob(null);
    setPaywallDismissed(false);
  };
  const endFreeRunReveal = () => {
    setFreeRunReveal(false);
    setViewerStart(null);
    // Still clipping → back to the scanner (the effect above reopens it);
    // done → the paywall.
    if (!freeRunRunning) setPaywallDismissed(false);
  };
  // A lock tapped while the rest are still clipping: show the paywall now;
  // closing it returns to the scanner.
  const freeRunLockTap = () => {
    if (!freeRunRunning) { endFreeRunReveal(); return; }
    setFreeRunReveal(false);
    setViewerStart(null);
    track("paywall_opened", { from: "free_run_early_clip" });
    setForcePaywall(true);
  };
  const [lockedPrice, setLockedPrice] = useState(24);
  useEffect(() => {
    if (!homeLocked) return;
    getRegion().then((r) => { if (r.discountPct > 0) setLockedPrice(Math.round(24 * (100 - r.discountPct)) / 100); });
  }, [homeLocked]);
  // Paywall gift still running? Show its first-month price + a live countdown.
  const [homeGiftEnds, setHomeGiftEnds] = useState(0);
  const [homeNow, setHomeNow] = useState(Date.now());
  useEffect(() => {
    if (!homeLocked) return;
    axiosInstance.get("/billing/exit-offer")
      .then((r) => { if (r.data?.active && r.data.expiresAt) setHomeGiftEnds(new Date(r.data.expiresAt).getTime()); })
      .catch(() => {});
  }, [homeLocked]);
  useEffect(() => {
    if (!homeGiftEnds) return undefined;
    const id = setInterval(() => setHomeNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [homeGiftEnds]);
  const homeGiftLeft = Math.max(0, homeGiftEnds - homeNow);
  const homeGiftPrice = homeGiftLeft > 0 && lockedPrice > 18 ? 18 : null;
  useEffect(() => {
    if (!needsPaywall || !paywallDismissed) return undefined;
    // Ignore the "ghost" click some phones fire right after the tap that
    // closed the paywall, or it would reopen instantly.
    const closedAt = Date.now();
    const reopen = (e) => {
      if (Date.now() - closedAt < 500) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest("[data-paywall-allow]")) return;
      e.preventDefault();
      e.stopPropagation();
      track("paywall_reopened");
      setPaywallDismissed(false);
    };
    document.addEventListener("click", reopen, true);
    return () => document.removeEventListener("click", reopen, true);
  }, [needsPaywall, paywallDismissed]);

  // Ended the trial early from the upgrade modal ("Start Pro now").
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pro") !== "started") return;
    toast.success("You're on Pro! 300 credits added.");
    params.delete("pro");
    const qs = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, []);

  // Back from Stripe (?paywall=paid): the webhook activates the plan a few
  // seconds after the redirect, so poll plan status instead of flashing the
  // paywall at someone who just paid.
  useEffect(() => {
    if (!awaitingPayment) return undefined;
    let tries = 0;
    let stopped = false;
    const done = () => {
      stopped = true;
      setAwaitingPayment(false);
      const sp = new URLSearchParams(window.location.search);
      sp.delete("paywall");
      sp.delete("trial");
      const next = sp.toString();
      window.history.replaceState({}, "", next ? `${window.location.pathname}?${next}` : window.location.pathname);
    };
    const check = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const res = await axiosInstance.get("/billing/plan/status");
        const plan = res.data?.plan || "none";
        const credits = res.data?.credits || 0;
        if (plan !== "none" || credits > 0) {
          // Credits BEFORE plan: this app renders with legacy ReactDOM.render
          // (no batching after an await), and a paid plan with the stale 0
          // remaining would pop the "credits running low" banner.
          if (typeof res.data?.generationsRemaining === "number") setGenerationsRemaining(res.data.generationsRemaining);
          if (typeof res.data?.creditsPeriodTotal === "number") setGenerationsTotal(res.data.creditsPeriodTotal);
          setPurchasedCredits(credits);
          setUserPlanInterval(res.data?.interval || null);
          setUserPlan(plan);
          // Their onboarding pick now opens in the clip box (first-pick effect
          // below, once userPlan flips); reload jobs for everything else.
          fetchJobs();
          if (new URLSearchParams(window.location.search).get("trial") === "1") {
            // Same event_id as the server's StartTrial, so Meta counts it once.
            trackTrialStartPixel(currentUser);
            track("trial_started", { source: "hosted_checkout", value: 1 });
            toast.success("You're on the Pro trial! 120 credits added.");
          } else {
            toast.success("You're in! Your plan is active.");
          }
          done();
          return;
        }
      } catch (_) { /* keep polling */ }
      if (tries >= 20) {
        toast("Payment is still processing. Refresh in a minute if this doesn't unlock.", { duration: 6000 });
        done();
        return;
      }
      setTimeout(check, 2000);
    };
    check();
    return () => { stopped = true; };
    // currentUser is only read for the StartTrial pixel; it mustn't restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingPayment]);

  // The stream they picked in onboarding (routes/freeRun.js): once the $1 trial
  // (or any plan) is active, open the clip box with it filled in, so they choose
  // how many minutes to clip and start a normal job from their credits. The
  // pick clears server-side when that job starts.
  const firstPickShownRef = useRef(false);
  const [firstClipPick, setFirstClipPick] = useState(false);
  useEffect(() => {
    if (firstPickShownRef.current || showOnboarding || !onboardingChecked) return;
    if (!userPlan || userPlan === "none") return;
    firstPickShownRef.current = true;
    axiosInstance.get("/free-run/pending").then(({ data }) => {
      const url = data?.pending?.videoUrl;
      if (!url) return;
      setInputMode("url");
      setVideoUrl(url);
      setFirstClipPick(true);
      setComposerOpen(true);
      track("first_clip_pick_opened", { platform: data.pending.platform || null });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlan, showOnboarding, onboardingChecked]);
  // Start the trimmer on what their credits cover (a trial's 120 = the first
  // 2 hours of a long stream) instead of the whole VOD; they can still change it.
  const firstPickRangedRef = useRef(false);
  useEffect(() => {
    if (!firstClipPick || firstPickRangedRef.current) return;
    if (!Number.isFinite(detectedDuration) || detectedDuration <= 0 || !(generationsRemaining > 0)) return;
    firstPickRangedRef.current = true;
    setRangeStart(0);
    setRangeEnd(Math.min(detectedDuration, generationsRemaining * 60));
  }, [firstClipPick, detectedDuration, generationsRemaining]);

  // Tap anywhere outside the inline input to collapse it back to the button
  // (no X needed). Deferred so the opening tap doesn't instantly close it.
  useEffect(() => {
    if (!composerOpen) return;
    const onDown = (e) => {
      if (heroInputRef.current && !heroInputRef.current.contains(e.target)) {
        setComposerOpen(false);
        setVideoUrl("");
      }
    };
    const t = setTimeout(() => document.addEventListener("pointerdown", onDown), 0);
    return () => { clearTimeout(t); document.removeEventListener("pointerdown", onDown); };
  }, [composerOpen]);

  // Once a file picked from the home hero lands in state, kick off the upload.
  useEffect(() => {
    if (pendingUpload && selectedFile) {
      setPendingUpload(false);
      withPhoneCheck(() => handleUpload());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpload, selectedFile]);

  // Animate placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [placeholders.length]);

  // Build the formatted streamer name from user's saved creator settings
  const getFormattedStreamerName = () => {
    if (!userChannelUsername) return "";
    if (userPlatform === "rumble") return `rumble.com/${userChannelUsername}`;
    if (userPlatform === "youtube") return `youtube.com/@${userChannelUsername}`;
    if (userPlatform === "twitch") return `twitch.tv/${userChannelUsername}`;
    if (userPlatform === "x") return `x.com/${userChannelUsername}`;
    if (userPlatform === "kick") return `kick.com/${userChannelUsername}`;
    if (userPlatform === "ganjing") return `ganjingworld.com/@${userChannelUsername}`;
    // OWN has no public web URL — content is app-only, so the
    // watermark just uses the @handle.
    if (userPlatform === "own") return `@${userChannelUsername}`;
    return userChannelUsername;
  };

const handleUrlChange = (e) => {
  setVideoUrl(e.target.value);
};

const handlePaste = () => {
  // Intentionally a no-op — users sometimes want to adjust settings
  // (content type, aspect ratio, max clips) before generating, so paste
  // no longer auto-fires generation. The input still receives the pasted
  // value via the native onChange handler.
};
  // Fetch all jobs on mount
  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    const t0 = performance.now();
    try {
      const res = await axiosInstance.get("/clips/jobs");
      const fetchedJobs = res.data.jobs || [];
      const ms = Math.round(performance.now() - t0);
      const bytes = JSON.stringify(res.data).length;
      const clipCount = fetchedJobs.reduce((n, j) => n + (j.clips?.length || 0), 0);
      console.log(`[lib] /clips/jobs → ${fetchedJobs.length} jobs / ${clipCount} clips · ${(bytes / 1024).toFixed(0)}KB · round-trip ${ms}ms` +
        (res.headers?.["x-jobs-query-ms"] ? ` (server query ${res.headers["x-jobs-query-ms"]}ms)` : ""));
      setJobs(fetchedJobs);
    } catch (err) {
      console.error(`[lib] /clips/jobs FAILED after ${Math.round(performance.now() - t0)}ms:`, err?.message || err);
    } finally {
      setJobsLoading(false);
      setJobsLoadedOnce(true);
    }
  }, []);

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // The try run is started mid-onboarding, after the mount fetch: load it as
  // soon as onboarding ends (and keep checking until it shows up), so the
  // clipping screen has its progress and stream.
  const freeRunLoaded = !freeRunPendingId || jobs.some((j) => j._id === freeRunPendingId);
  useEffect(() => {
    if (freeRunLoaded || showOnboarding) return undefined;
    fetchJobs();
    const id = setInterval(fetchJobs, 4000);
    return () => clearInterval(id);
  }, [freeRunLoaded, showOnboarding, fetchJobs]);

  // Coach Mode music catalog — fetched once, kept in memory so every
  // panel filters client-side and no card ever shows an empty Music
  // section while the network is in flight.
  const [musicCatalog, setMusicCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/music-tracks/catalog")
      .then((r) => { if (!cancelled) setMusicCatalog(r.data?.tracks || []); })
      .catch(() => { /* panel falls back to its prop default */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch video titles for YouTube jobs only
  useEffect(() => {
    jobs.forEach(job => {
      if (job.videoUrl && job.platform === "youtube" && videoTitles[job._id] === undefined) {
        fetchVideoInfo(job.videoUrl, job._id);
      }
    });
  }, [jobs, fetchVideoInfo, videoTitles]);

  // Request notification permission when user has active jobs
  useEffect(() => {
    const activeJobs = jobs.filter(j => j.status === "queued" || j.status === "processing");
    if (activeJobs.length === 0) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [jobs]);

  // iOS keyboard fix: keep the fixed bottom bar above the virtual keyboard
  // by tracking visualViewport height changes and setting a CSS variable.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty('--keyboard-inset', `${kbHeight}px`);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--keyboard-inset');
    };
  }, []);

  // Poll for status updates on active jobs
  useEffect(() => {
    const activeJobs = jobs.filter(j => j.status === "queued" || j.status === "processing");
    if (activeJobs.length === 0) return;

    // Immediate poll function for new jobs
    const pollJob = async (job) => {
      try {
        const res = await axiosInstance.get("/clips/status/" + job._id);
        const {
          status,
          progress,
          clips: jobClips,
          currentStep,
          // Rumble metadata from worker
          videoTitle,
          videoThumbnail,
          videoAuthor,
          videoDuration,
          // Time-remaining estimate (seconds) from worker
          etaSeconds
        } = res.data;

        // Update job in state
        setJobs(prev => prev.map(j => {
          if (j._id === job._id) {
            const hasNewMetadata = (videoTitle && !j.videoTitle) || (videoThumbnail && !j.videoThumbnail);
            const updated = {
              ...j,
              status,
              progress: progress || 0,
              currentStep,
              clips: jobClips || j.clips,
              // Include Rumble metadata from worker
              videoTitle: videoTitle || j.videoTitle,
              videoThumbnail: videoThumbnail || j.videoThumbnail,
              videoAuthor: videoAuthor || j.videoAuthor,
              videoDuration: videoDuration || j.videoDuration,
              etaSeconds: typeof etaSeconds === "number" ? etaSeconds : j.etaSeconds,
              _metadataLoaded: hasNewMetadata || j._metadataLoaded
            };

            // Check if just completed
            if (status === "completed" && j.status !== "completed" && jobClips?.length > 0) {
              toast.success(t('clipsReady', { count: jobClips.length }));
              if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Your clips are ready!", {
                  body: jobClips.length === 1
                    ? "1 clip is ready to view."
                    : jobClips.length + " clips are ready to view.",
                  icon: "/logo192.png",
                });
              }
            } else if (status === "failed" && j.status !== "failed") {
              // Failed card shown in grid — no toast needed
            }
            return updated;
          }
          return j;
        }));

        // Update selected job if it's the one being viewed
        if (selectedJob?._id === job._id) {
          setSelectedJob(prev => ({
            ...prev,
            status,
            progress: progress || 0,
            currentStep,
            clips: jobClips || prev?.clips,
            videoTitle: videoTitle || prev?.videoTitle,
            videoThumbnail: videoThumbnail || prev?.videoThumbnail,
            videoAuthor: videoAuthor || prev?.videoAuthor,
            videoDuration: videoDuration || prev?.videoDuration,
            etaSeconds: typeof etaSeconds === "number" ? etaSeconds : prev?.etaSeconds
          }));

          // Auto-switch from progress modal to clips modal when job completes
          if (status === "completed" && jobClips?.length > 0) {
            setShowProgressModal(false);
            setShowClipsModal(true);
          }
        }
      } catch (err) {
        console.error("Status poll error:", err);
      }
    };

    // Poll immediately for jobs without metadata (Rumble)
    activeJobs.forEach(job => {
      if (job.platform === "rumble" && !job.videoTitle && !job._metadataLoaded) {
        pollJob(job);
      }
    });

    const interval = setInterval(async () => {
      for (const job of activeJobs) {
        await pollJob(job);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobs, selectedJob]);

// Live channel URL → start the session and go STRAIGHT into the live Sherlock
// scanner (no confirmation popup). Handles the Pro gate + common errors.
// The server reported "this isn't live". A YouTube /live/<id> URL keeps
  // serving the REPLAY after the broadcast ends — that's the single most common
  // way users land here (they paste yesterday's stream) — so clip it as a normal
  // VOD instead of dead-ending. Twitch/Kick channel URLs have no replay at the
  // same URL, so those just get told the channel is offline.
  const handleLiveNotLive = (link) => {
    setLiveModalOpen(false);
    if (AMBIGUOUS_LIVE_URL.test(link || "")) {
      toast.success("That stream already ended — clipping the replay instead.");
      handleGenerate(link, { forceVod: true });
    } else {
      toast.error("That channel isn't live right now. Paste a VOD link to clip a past stream.");
    }
  };

const startLiveFromUrl = async (link) => {
    // Don't paywall on the URL alone: a YouTube /live/<id> link is a live stream
    // OR an ended one's replay, and clipping a replay is included in every paid
    // plan. Let the server probe first (it answers not_live for a finished
    // broadcast). Twitch/Kick channel URLs are live-or-nothing, so those keep
    // the instant upsell.
    if (!["Pro", "Premium"].includes(userPlan) && !AMBIGUOUS_LIVE_URL.test(link || "")) {
      openUpgrade(); return;
    }
    setComposerOpen(false);
    // Optimistically open the live Sherlock scanner IMMEDIATELY — no waiting on
    // the round-trip. If the start fails we tear it back down below.
    setActiveLiveSession({
      _id: "pending", status: "starting", channelUrl: link, clips: [],
      streamerName: (link.split("/").filter(Boolean).pop() || ""),
    });
    setLiveDismissed(false);
    setLiveScannerOpen(true);
    try {
      await axiosInstance.post("/live/start", {
        videoUrl: link,
        aspectRatio,
        burnHookTitle: effectiveBurnHookTitle,
        burnSubtitles: effectiveBurnSubtitles,
        burnWatermark,
      });
      await refreshActiveLive(); // swap the optimistic session for the real one
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "live_already_running") { await refreshActiveLive(); return; }
      // Failed → close the scanner and explain.
      setActiveLiveSession(null);
      setLiveScannerOpen(false);
      if (code === "not_live") handleLiveNotLive(link);
      else if (code === "pro_required") openUpgrade();
      else if (code === "out_of_credits") { toast.error("You're out of credits."); openUpgrade(); }
      else toast.error(err.response?.data?.message || err.response?.data?.error || "Couldn't start live clipping");
    }
  };

const handleGenerate = async (urlOverride, opts = {}) => {
    if (currentUser?.isDemo) {
      toast.error("Demo accounts can't generate clips. Log out and sign up to create your own.", { duration: 5000 });
      return;
    }

    // A link we already know can't be downloaded — say why instead of opening
    // the paywall or starting a run that will fail.
    if (urlProbeError && (urlOverride || videoUrl) === urlProbeError.url) {
      toast.error(urlProbeError.message, { id: "url-probe-error", duration: 8000 });
      return;
    }

    // Hard paywall: an unpaid user with no purchased credits can't generate —
    // show the pricing popup instead of calling the API. (Free users have no
    // plan AND no credits.) userPlan === null = still loading, so don't gate
    // prematurely; the backend also enforces.
    if (userPlan === "none" && !(purchasedCredits > 0)) {
      openUpgrade();
      return;
    }

    const url = urlOverride || videoUrl;
    if (!url) return toast.error(t('pasteVideoLinkFirst'), { duration: 3500 });
    if (!contentType) return toast.error(t('selectContentTypeFirst'), { duration: 3500 });
    if (!userPlatform) return toast.error(t('selectPlatformInSettings'), { duration: 3500 });

    // Live channel URLs → continuous Live Clipping (Pro feature). Twitch/Kick
    // channel pages are always-live; YouTube counts only when the URL is a
    // /live link (a bare watch?v= could be an ended stream = a VOD).
    const isTwitchLive = url.includes("twitch.tv/") && !url.includes("/videos/");
    const isKickLive = url.includes("kick.com/") && !url.includes("/videos/");
    const isYouTubeLive = (url.includes("youtube.com") || url.includes("youtu.be")) && /\/live(\/|\?|$)/.test(url);
    // opts.forceVod = the live path already ran and the server said the stream
    // isn't live (see handleLiveNotLive) — clip it as a normal VOD instead of
    // bouncing back into live clipping forever.
    if (!opts.forceVod && (isTwitchLive || isKickLive || isYouTubeLive)) {
      // Go straight into the live Sherlock scanner — no confirmation popup.
      startLiveFromUrl(url);
      return;
    }

    const formattedStreamerName = getFormattedStreamerName();
    if (!formattedStreamerName) return toast.error(t('setChannelUsername'), { duration: 3500 });

    // Detect platform from URL so Rumble links work even if user's profile is set to YouTube
    const detectPlatformFromUrl = (u) => {
      if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
      if (u.includes("rumble.com")) return "rumble";
      if (u.includes("twitch.tv")) return "twitch";
      if (u.includes("kick.com")) return "kick";
      if (u.includes("twitter.com") || u.includes("x.com")) return "x";
      return userPlatform;
    };
    const detectedPlatform = detectPlatformFromUrl(url);

    // Show the Sherlock scanner INSTANTLY (before the API call) so there's no
    // fetching gap. jobId is filled in once /clips/generate responds.
    setScannerJob({ jobId: null, brandImage: brandProfilePicture, title: formattedStreamerName, videoUrl: url });
    setScannerOpen(true);
    setNewClips(0);
    setComposerOpen(false);
    setVideoUrl("");

    setLoading(true);
    try {
      const res = await axiosInstance.post("/clips/generate", {
        videoUrl: url,
        streamerName: formattedStreamerName,
        platform: detectedPlatform,
        contentType,
        // ClipFinder is guidance-only — aspect ignored, renderRequested
        // always false. Clipper carries the only render path.
        aspectRatio: genMode === "coach" ? "9:16" : aspectRatio,
        maxClips,
        mode: genMode,
        renderRequested: false,
        // Effective values respect the Clipper-16:9 carve-out (title +
        // subs forced off in that combo since they can't be burned in).
        burnHookTitle: effectiveBurnHookTitle,
        burnSubtitles: effectiveBurnSubtitles,
        burnWatermark,
        // Source trim window — only sent when the user picked a sub-range of a
        // detected-duration video (otherwise the whole video is processed).
        ...(Number.isFinite(detectedDuration) && rangeStart != null &&
          (rangeStart > 0 || rangeEnd < detectedDuration - 1)
            ? { clipRangeStartSec: Math.floor(rangeStart), clipRangeEndSec: Math.floor(rangeEnd) }
            : {}),
        // Detected length → the server holds only the credits this video needs
        // instead of the whole balance until the worker reports it.
        ...(Number.isFinite(detectedDuration) && detectedDuration > 0 ? { durationSec: Math.ceil(detectedDuration) } : {}),
        ...(campaignRunRef.current?.campaignId && campaignRunRef.current.url === url
          ? { campaignId: campaignRunRef.current.campaignId } : {}),
      });

      // Add new job to the list
      const newJob = {
        _id: res.data.jobId,
        videoUrl: url,
        streamerName: formattedStreamerName,
        platform: detectedPlatform,
        brandId: activeBrandId,
        status: "queued",
        progress: 0,
        maxClips,
        mode: genMode,
        renderRequested: false,
        aspectRatio,
        createdAt: new Date().toISOString(),
      };

      setJobs(prev => [newJob, ...prev]);
      updateStreak();
      setGenerationsRemaining(prev => prev !== null ? Math.max(0, prev - 1) : null);

      // Persistent Sherlock-style scanner tracks THIS job until all clips are
      // out, then hands off to the swipe viewer. Shows the active brand image.
      // Attach the real jobId so the scanner starts tracking progress.
      setScannerJob(prev => (prev ? { ...prev, jobId: res.data.jobId } : { jobId: res.data.jobId, brandImage: brandProfilePicture, title: formattedStreamerName }));

      // Fetch video title for YouTube only
      if (detectedPlatform === "youtube") {
        fetchVideoInfo(url, res.data.jobId);
      }

      // No "Clipping Started!" toast: the clipping screen is already showing.
      toast.dismiss();
      setVideoUrl("");
    } catch (err) {
      // The optimistic scanner must go away if the run never started.
      setScannerJob(null);
      setScannerOpen(false);
      const errorCode = err.response?.data?.error;
      if (errorCode === "is_live" && !opts.forceVod) {
        // A YouTube watch?v= / youtu.be link that's live right now (only /live
        // URLs are caught above) — clip it with Live Clipping instead. After a
        // forceVod bounce (live said "not live", YouTube still says live) fall
        // through to the toast so the two can't ping-pong.
        setLoading(false);
        startLiveFromUrl(url);
        return;
      } else if (errorCode === "free_limit") {
        // Free trial user — show upgrade modal
        openUpgrade();
      } else if (errorCode === "out_of_credits") {
        showOutOfCredits();
      } else if (errorCode === "limit_reached") {
        // Paid user hit plan limit — show limit reached modal with reset date
        setLimitMessage(
          err.response?.data?.message ||
            t('reachedUploadLimit')
        );
        try {
          const planRes = await axiosInstance.get("/billing/plan/status");
          if (planRes.data?.currentPeriodEnd) {
            setLimitResetDate(new Date(planRes.data.currentPeriodEnd));
          } else {
            setLimitResetDate(null);
          }
        } catch (_) {
          setLimitResetDate(null);
        }
        setShowLimitModal(true);
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || t('failedToStartClipping'));
      }
    }
    setLoading(false);
  };
  const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["video/mp4", "video/quicktime"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('onlyMp4MovSupported'));
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('fileMustBeUnder2GB'));
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const allowedTypes = ["video/mp4", "video/quicktime"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('onlyMp4MovSupported'));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('fileMustBeUnder2GB'));
      return;
    }

    setInputMode("upload");
    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleUpload = async () => {
    if (currentUser?.isDemo) {
      toast.error("Demo accounts can't generate clips. Log out and sign up to create your own.", { duration: 5000 });
      return;
    }
    // Hard paywall (see handleGenerate): generating is a paid action.
    if (userPlan === "none" && !(purchasedCredits > 0)) {
      openUpgrade();
      return;
    }
    if (!selectedFile) return toast.error(t('selectVideoFileFirst'), { duration: 3500 });
    if (!contentType) return toast.error(t('selectContentTypeFirst'), { duration: 3500 });
    if (!userPlatform) return toast.error(t('selectPlatformInSettings'), { duration: 3500 });

    const formattedStreamerName = getFormattedStreamerName();
    if (!formattedStreamerName) return toast.error(t('setChannelUsername'), { duration: 3500 });

    setLoading(true);
    setUploadProgress(0);

    try {
      // 1. Get presigned URL
      const urlRes = await axiosInstance.post("/clips/upload-url", {
        filename: selectedFile.name,
        fileType: selectedFile.type,
      });

      const { url: presignedUrl, s3Key } = urlRes.data;

      // 2. Upload directly to S3 with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignedUrl);
        xhr.setRequestHeader("Content-Type", selectedFile.type);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(selectedFile);
      });

      // 3. Create the clip job
      const res = await axiosInstance.post("/clips/generate-from-upload", {
        s3Key,
        filename: selectedFile.name,
        fileSize: selectedFile.size,
        streamerName: formattedStreamerName,
        platform: userPlatform,
        contentType,
        aspectRatio: genMode === "coach" ? "9:16" : aspectRatio,
        maxClips,
        mode: genMode,
        renderRequested: false,
        burnHookTitle: effectiveBurnHookTitle,
        burnSubtitles: effectiveBurnSubtitles,
        burnWatermark,
      });

      const newJob = {
        _id: res.data.jobId,
        videoUrl: "",
        streamerName: formattedStreamerName,
        platform: userPlatform,
        brandId: activeBrandId,
        videoSource: "upload",
        originalFilename: selectedFile.name,
        status: "queued",
        progress: 0,
        maxClips,
        aspectRatio,
        createdAt: new Date().toISOString(),
      };

      setJobs(prev => [newJob, ...prev]);
      updateStreak();
      setGenerationsRemaining(prev => prev !== null ? Math.max(0, prev - 1) : null);
      setScannerJob({ jobId: res.data.jobId, brandImage: brandProfilePicture, title: selectedFile.name });
      setScannerOpen(true);
      setNewClips(0);
      setComposerOpen(false);
      toast.dismiss();
      toast.success(t('uploadCompleteClippingStarted'));
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === "free_limit") {
        openUpgrade();
      } else if (errorCode === "out_of_credits") {
        showOutOfCredits();
      } else if (errorCode === "limit_reached") {
        setLimitMessage(err.response?.data?.message || t('reachedUploadLimit'));
        try {
          const planRes = await axiosInstance.get("/billing/plan/status");
          if (planRes.data?.currentPeriodEnd) setLimitResetDate(new Date(planRes.data.currentPeriodEnd));
          else setLimitResetDate(null);
        } catch (_) { setLimitResetDate(null); }
        setShowLimitModal(true);
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || t('uploadFailed'));
      }
    }
    setLoading(false);
  };

  // Handle job card tap. In-progress jobs open the Sherlock scanner (tracking
  // that job) instead of the old progress popup.
  const handleJobTap = (job) => {
    if (job.status === "queued" || job.status === "processing") {
      setScannerJob({ jobId: job._id, brandImage: brandProfilePicture, title: getVideoTitle(job) || job.streamerName });
      setScannerOpen(true);
      return;
    }
    setSelectedJob(job);
    if (job.status === "completed" && job.clips?.length > 0) {
      setShowClipsModal(true);
      setShowProgressModal(false);
    }
  };

  const closeModals = () => {
    setShowProgressModal(false);
    setShowClipsModal(false);
    setSelectedJob(null);
  };

  const handleGenerateAnother = (url) => {
    closeModals();
    setVideoUrl(url);
    setInputMode("url");
    setTimeout(() => handleGenerate(url), 50);
  };

  // Unified open handler — Clipper tile click + ClipFinder tile click
  // both land here. The right panel renders based on job.mode.
  const handleClipClick = (clip, job, clipIndex) => {
    setSelectedClipDetail({ clip, job, clipIndex });
  };

  const toggleSelect = (clipKey, e) => {
    e.stopPropagation();
    setSelectedClipIds(prev =>
      prev.includes(clipKey) ? prev.filter(id => id !== clipKey) : [...prev, clipKey]
    );
  };

  const toggleLike = (clipKey, e) => {
    e.stopPropagation();
    setLikedClipIds(prev => {
      const next = prev.includes(clipKey) ? prev.filter(id => id !== clipKey) : [...prev, clipKey];
      localStorage.setItem('clipbait_liked', JSON.stringify(next));
      return next;
    });
  };

  const handleQuickDownload = async (clip, job, clipIndex, e) => {
    e.stopPropagation();
    try {
      const res = await axiosInstance.get("/clips/download-url", {
        params: { url: clip.url, filename: clip.hook || `clip-${clipIndex + 1}` },
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleDeleteClip = (clip, job, clipIndex, e) => {
    e.stopPropagation();
    setClipToDelete({ clip, job, clipIndex });
    setShowDeleteModal(true);
  };

  const handleBulkDownload = async () => {
    const selected = allClips.filter(({ job, clipIndex }) =>
      selectedClipIds.includes(`${job._id}-${clipIndex}`)
    );
    for (const { clip, job, clipIndex } of selected) {
      try {
        const res = await axiosInstance.get("/clips/download-url", {
          params: { url: clip.editedUrl || clip.url, filename: clip.hook || `clip-${clipIndex + 1}` },
        });
        window.open(res.data.url, '_blank');
        await new Promise(r => setTimeout(r, 600));
      } catch {}
    }
    setSelectedClipIds([]);
  };

  const handleBulkDelete = () => {
    const targets = allClips.filter(({ job, clipIndex }) =>
      selectedClipIds.includes(`${job._id}-${clipIndex}`)
    );
    if (targets.length === 0) return;
    setBulkDeleteTargets(targets);
    setShowDeleteModal(true);
  };

  // Handle job deletion - show modal
  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleRetryJob = async (job) => {
    try {
      const res = await axiosInstance.post("/clips/generate", {
        videoUrl: job.videoUrl,
        streamerName: job.streamerName,
        platform: job.platform,
        contentType: job.contentType || contentType,
        aspectRatio: job.aspectRatio || aspectRatio,
        maxClips: job.maxClips || maxClips,
      });
      const newJob = {
        _id: res.data.jobId,
        videoUrl: job.videoUrl,
        streamerName: job.streamerName,
        platform: job.platform,
        brandId: job.brandId || activeBrandId,
        status: "queued",
        progress: 0,
        maxClips: job.maxClips || maxClips,
        aspectRatio: job.aspectRatio || aspectRatio,
        createdAt: new Date().toISOString(),
      };
      // Replace the failed job with the new one
      setJobs(prev => [newJob, ...prev.filter(j => j._id !== job._id)]);
      setGenerationsRemaining(prev => prev !== null ? Math.max(0, prev - 1) : null);
      toast.dismiss();
      toast.success(t('clippingStarted'));
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === "free_limit") openUpgrade();
      else if (errorCode === "out_of_credits") showOutOfCredits();
      else toast.error(err.response?.data?.message || t('failedToStartClipping'));
    }
  };

  const handleDeleteFailedJob = (jobId) => {
    const job = jobs.find(j => j._id === jobId);
    if (job) {
      setJobToDelete(job);
      setShowDeleteModal(true);
    }
  };

  // Confirm deletion from modal
  const confirmDeleteJob = async () => {
    // Bulk clip deletion
    if (bulkDeleteTargets?.length > 0) {
      const byJob = {};
      for (const { job, clipIndex } of bulkDeleteTargets) {
        if (!byJob[job._id]) byJob[job._id] = { job, indices: [] };
        byJob[job._id].indices.push(clipIndex);
      }
      let totalDeleted = 0;
      let liveTouched = false;
      for (const { job, indices } of Object.values(byJob)) {
        // Live-session clips delete through the live endpoint (high→low so
        // splices don't shift remaining indices). Never delete the session.
        if (job.isLive) {
          for (const idx of [...indices].sort((a, b) => b - a)) {
            try {
              await axiosInstance.delete(`/live/${job._id}/clips/${idx}`);
              totalDeleted += 1;
              liveTouched = true;
            } catch (err) {
              console.error(`Failed to delete live clip ${job._id}/${idx}:`, err);
            }
          }
          continue;
        }
        const allSelected = (job.clips || []).every((_, i) => indices.includes(i));
        if (allSelected) {
          try {
            await axiosInstance.delete(`/clips/jobs/${job._id}`);
            setJobs(prev => prev.filter(j => j._id !== job._id));
            totalDeleted += indices.length;
          } catch { toast.error(t('failedToDeleteProject')); }
        } else {
          // Partial selection — delete clips one at a time on the server.
          // Sort high→low so splicing earlier indices doesn't shift the
          // later ones (server uses splice).
          const sorted = [...indices].sort((a, b) => b - a);
          let okIndices = [];
          for (const idx of sorted) {
            try {
              await axiosInstance.delete(`/clips/jobs/${job._id}/clips/${idx}`);
              okIndices.push(idx);
              totalDeleted += 1;
            } catch (err) {
              console.error(`Failed to delete clip ${job._id}/${idx}:`, err);
            }
          }
          // Mirror the server-side deletions in local state
          if (okIndices.length > 0) {
            setJobs(prev => prev.map(j => {
              if (j._id !== job._id) return j;
              return { ...j, clips: (j.clips || []).filter((_, i) => !okIndices.includes(i)) };
            }));
          }
        }
      }
      if (liveTouched) await refreshActiveLive();
      if (totalDeleted > 0) {
        toast.success(t('clipsDeleted', { count: totalDeleted }));
      } else {
        toast.error(t('failedToDeleteProject'));
      }
      setSelectedClipIds([]);
      setBulkDeleteTargets(null);
      setShowDeleteModal(false);
      return;
    }

    // Single clip deletion (multi-clip job)
    if (clipToDelete) {
      const { job, clipIndex } = clipToDelete;
      // Live-session clips delete through the live endpoint (removing the last
      // one keeps the session alive). Re-pull sessions to resync the feed.
      if (job.isLive) {
        try {
          await axiosInstance.delete(`/live/${job._id}/clips/${clipIndex}`);
          await refreshActiveLive();
          toast.success("Clip removed");
        } catch (err) {
          toast.error(err.response?.data?.error || "Failed to delete clip");
        }
        setClipToDelete(null);
        setShowDeleteModal(false);
        return;
      }
      if ((job.clips || []).length <= 1) {
        try {
          await axiosInstance.delete(`/clips/jobs/${job._id}`);
          setJobs(prev => prev.filter(j => j._id !== job._id));
          if (selectedJob?._id === job._id) closeModals();
        } catch (err) {
          toast.error(err.response?.data?.error || t('failedToDeleteProject'));
        }
      } else {
        try {
          await axiosInstance.delete(`/clips/jobs/${job._id}/clips/${clipIndex}`);
          setJobs(prev => prev.map(j => {
            if (j._id !== job._id) return j;
            const newClips = [...(j.clips || [])];
            newClips.splice(clipIndex, 1);
            return { ...j, clips: newClips };
          }));
          toast.success("Clip removed");
        } catch (err) {
          console.error("Failed to delete clip:", err);
          toast.error(err.response?.data?.error || "Failed to delete clip");
        }
      }
      setClipToDelete(null);
      setShowDeleteModal(false);
      return;
    }

    // Whole job deletion
    if (!jobToDelete) return;
    const wasProcessing = jobToDelete.status === "queued" || jobToDelete.status === "processing";
    try {
      await axiosInstance.delete(`/clips/jobs/${jobToDelete._id}`);
      setJobs(prev => prev.filter(j => j._id !== jobToDelete._id));
      toast.success(wasProcessing ? t('jobCancelled') : t('projectDeleted'));
      if (selectedJob?._id === jobToDelete._id) closeModals();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToDeleteProject'));
    }
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
    setClipToDelete(null);
    setBulkDeleteTargets(null);
  };

  // Helper to get video title - YouTube from oEmbed, Rumble from worker
  const getVideoTitle = (job) => {
    if (job.platform === "youtube") {
      return videoTitles[job._id];
    }
    // Rumble: use worker metadata
    return job.videoTitle;
  };

  // Helper to get video thumbnail - YouTube from oEmbed, Rumble from worker
  const getVideoThumb = (job) => {
    if (job.platform === "youtube" && videoThumbs[job._id]) {
      return videoThumbs[job._id];
    }
    if (job.videoThumbnail) return job.videoThumbnail;
    // Fallback to active brand's avatar, then user's profile picture
    return brandProfilePicture || currentUser?.profilePicture || null;
  };

  // Helper to format duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  // Separate jobs by status for display (exclude cancelled jobs)
  const allJobsVisible = jobs.filter(j => j.status !== "cancelled");
  const hasAnyJobs = allJobsVisible.length > 0;

  // Filter jobs to the active brand.
  // Primary: match by brandId (set on all new jobs). Fallback for legacy jobs: streamerName match.
  const activeBrandStreamerName = getFormattedStreamerName();
  const brandJobs = activeBrandId
    ? jobs.filter(j => {
        if (j.brandId) return j.brandId === activeBrandId || j.brandId?.toString() === activeBrandId?.toString();
        // Legacy job (no brandId) — fall back to streamerName
        return activeBrandStreamerName && j.streamerName?.toLowerCase() === activeBrandStreamerName.toLowerCase();
      })
    : (activeBrandStreamerName
        ? jobs.filter(j => j.streamerName?.toLowerCase() === activeBrandStreamerName.toLowerCase())
        : []);

  // Live sessions, scoped to the active brand the same way VOD jobs are.
  const brandLiveSessions = activeBrandId
    ? (liveSessions || []).filter(s => {
        if (s.brandId) return String(s.brandId) === String(activeBrandId);
        return activeBrandStreamerName && s.streamerName?.toLowerCase() === activeBrandStreamerName.toLowerCase();
      })
    : (liveSessions || []);

  // Newest first — so freshly rendered clips/runs populate at the TOP of the
  // grid instead of the bottom (the server returns them oldest-first).
  const byNewest = (a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

  const activeJobs = brandJobs.filter(j => j.status === "queued" || j.status === "processing");
  // In demo mode the demo run is shown as a live session instead (below).
  const completedJobs = brandJobs
    .filter(j => j.status === "completed" && !(demoJob && String(j._id) === String(demoJob._id)))
    .sort(byNewest);

  // Demo mode's stand-in "live session", shaped like a real one so the live
  // card, scanner and pill render unchanged. Clips appear as they're revealed.
  const demoRevealed = demoJob ? Math.min(demoLive.revealed, demoJob.clips?.length || 0) : 0;
  const demoSession = demoJob
    ? {
        _id: "demo-live",
        isDemo: true,
        status: "active",
        platform: platformFromUrl(demoJob.videoUrl),
        videoTitle: demoJob.videoTitle,
        videoThumbnail: demoJob.videoThumbnail || youtubeThumb(demoJob.videoUrl),
        streamerName: demoJob.streamerName,
        clips: (demoJob.clips || []).slice(0, demoRevealed),
        createdAt: new Date().toISOString(),
      }
    : null;
  // What the live UI shows: a real running session wins over the demo.
  const shownLiveSession = activeLiveSession || demoSession;
  // Failed runs are refunded automatically; their cards are hidden from the
  // Library for now (flip to show Retry / Delete cards again).
  const SHOW_FAILED_JOBS = false;
  const failedJobs = brandJobs.filter(j => j.status === "failed").sort(byNewest);

  // Live clipping sessions surface their clips in the SAME feed as VOD clips:
  // each session becomes a job-like object (its _id resolves through
  // /clips/status, so click→detail→edit→download all work unchanged). clipIndex
  // must match the session.clips order the backend returns.
  const liveJobs = brandLiveSessions
    .filter((s) => (s.clips?.length || 0) > 0)
    .map((s) => ({
      _id: s._id,
      status: "completed",
      mode: "streamer",
      isLive: true,
      aspectRatio: s.aspectRatio || "9:16",
      createdAt: s.lastClipAt || s.createdAt,
      clips: s.clips || [],
      videoTitle: s.videoTitle,
      streamerName: s.streamerName,
    }));
  // Demo run as a live job: its _id is the real job, so tapping a clip opens
  // the real clip detail / editor / download. Only revealed clips show.
  if (demoJob && demoRevealed > 0) {
    liveJobs.push({
      ...demoJob,
      isLive: true,
      isDemo: true,
      createdAt: new Date().toISOString(),
      clips: (demoJob.clips || []).slice(0, demoRevealed),
    });
  }

  // Flat list of all clips — newest job first (completed + any clips already
  // rendered in active jobs + live-session clips), so new clips appear at top.
  const allClips = [
    ...completedJobs,
    ...activeJobs.filter(j => j.clips?.length > 0),
    ...liveJobs,
  ]
    .sort(byNewest)
    .flatMap(job => {
      // clipIndex stays the TRUE backend position (so click→detail→download
      // resolve correctly). For live sessions the worker appends newest last,
      // so sort the display newest-first by broadcastStartSec.
      const mapped = (job.clips || []).map((clip, clipIndex) => ({ clip, job, clipIndex }));
      if (job.isDemo) {
        // Newest-revealed clip at the top, like a live session.
        mapped.sort((a, b) => b.clipIndex - a.clipIndex);
      } else if (job.isLive) {
        mapped.sort((a, b) => (b.clip.broadcastStartSec || 0) - (a.clip.broadcastStartSec || 0));
      }
      return mapped;
    });

  // Live state of the job the clipping scanner is tracking.
  const trackedJob = scannerJob?.jobId ? jobs.find(j => j._id === scannerJob.jobId) : null;
  const scannerProgress = trackedJob?.progress || 0;
  const scannerResults = trackedJob?.clips?.length || 0;
  const scannerDone = trackedJob?.status === "completed";
  const scannerFailed = trackedJob?.status === "failed";

  // While a run is active, the Library tab badge reflects clips produced so far.
  useEffect(() => {
    if (scannerJob?.jobId) setNewClips(scannerResults);
  }, [scannerResults, scannerJob?.jobId]);

  // Live auto-clip clips also count as "new" until the Library is opened. A
  // baseline ref means clips that already existed when the page loaded don't
  // count — only ones that arrive while you're watching another tab.
  // Count the running session OR the just-ended one, so the "new clips" badge
  // survives the session ending (out of credits / stream ended) until the user
  // opens the Library — it shouldn't vanish with the scanner.
  const liveSessionForBadge = activeLiveSession || endedLiveSession;
  const liveClipCount = liveSessionForBadge?.clips?.length || 0;
  const liveBaselineRef = useRef(null);
  const [liveNew, setLiveNew] = useState(0);
  useEffect(() => {
    if (!liveSessionForBadge) { liveBaselineRef.current = null; setLiveNew(0); return; }
    if (liveBaselineRef.current === null) { liveBaselineRef.current = liveClipCount; return; }
    if (liveClipCount > liveBaselineRef.current) {
      const delta = liveClipCount - liveBaselineRef.current;
      liveBaselineRef.current = liveClipCount;
      if (activeTab !== "library") setLiveNew((n) => n + delta);
    }
  }, [liveSessionForBadge, liveClipCount, activeTab]);

  // Opening the Library = "viewing the results": clear the badge, and once the
  // job is done, dismiss the scanner for good (a new run makes a new one).
  useEffect(() => {
    if (activeTab !== "library") return;
    setNewClips(0);
    setLiveNew(0);
    if (scannerJob && scannerDone && !(freeRunAwaitingReveal && scannerJob.jobId === freeRunJob?._id)) {
      setScannerJob(null);
      setScannerOpen(false);
    }
    // Viewing the library = the results were seen; dismiss any ended-session card.
    if (endedLiveSession) {
      dismissedEndedIdRef.current = String(endedLiveSession._id);
      setEndedLiveSession(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scannerDone]);

  // Mirror the new-clip count to both nav badges (desktop Leftbar + mobile bar).
  useEffect(() => {
    try {
      const total = activeTab === "library" ? 0 : newClips + liveNew;
      localStorage.setItem("cb_new_clips", String(total));
      window.dispatchEvent(new Event("cb_new_clips"));
    } catch { /* ignore */ }
  }, [newClips, liveNew, activeTab]);

  // Select-all / clear toggle. Must come AFTER `allClips` is declared.
  const allClipKeys = allClips.map(({ job, clipIndex }) => `${job._id}-${clipIndex}`);
  const allSelected = allClipKeys.length > 0 && selectedClipIds.length === allClipKeys.length;
  const toggleSelectAll = () => {
    setSelectedClipIds(allSelected ? [] : allClipKeys);
  };

  // Flow: Register → Onboarding → Pricing popup. Onboarding shows right after
  // signup for anyone who hasn't completed it (regardless of plan), so we
  // collect their info first. When they finish, we drop them into the app and
  // — if they're not on a paid plan — pop the pricing modal.
  if (showOnboarding) {
    return (
      <Onboarding
        onComplete={(data) => {
          setShowOnboarding(false);
          if (data?.phoneSaved) setUserPhoneNumber("saved"); // asked in onboarding
          if (data?.freeRunJobId) {
            setFreeRunPendingId(String(data.freeRunJobId));
            if (data.freeRunVideoUrl) setFreeRunVideoUrl(data.freeRunVideoUrl);
          }
          // Their onboarding photo (face) is the picture in the clipping ring.
          if (data?.photoUrl) setBrandProfilePicture(data.photoUrl);
          const alreadyPaid = !!userPlan && userPlan !== "none";
          if (data?.referralBonusStatus === "pending" && !alreadyPaid) {
            // OWN partner signup → show the (Pro-only) plans popup first to
            // convert them. The welcome popup only appears if they exit without
            // subscribing (handled in handleUpgradeClose). Skip entirely if they
            // already went Pro (e.g. paid via the marketing funnel).
            setReferralBonusStatus("pending");
            setShowUpgradeModal(true);
          } else if (data?.referralSource === "russell" && !alreadyPaid) {
            setShowRussellOffer(true);
          }
          // Everyone else who hasn't paid lands on the hard paywall below.
        }}
      />
    );
  }

  // Hard paywall (see needsPaywall above). Closable; reopens on any action.
  if (awaitingPayment) {
    return <HardPaywall activating />;
  }
  // Free run failed → pick another stream, same screen as onboarding.
  if (freeRunRetry) {
    return (
      <div className="ob">
        <div className="ob-screen ob-in-right">
          <TryFree
            retry
            onStarted={(jobId, url) => {
              // Unpaid: the pick waits for the $1 trial; the paywall shows it.
              if (!jobId) { setFreeRunRetry(false); setPaywallDismissed(false); return; }
              setFreeRunPendingId(String(jobId));
              setFreeRunVideoUrl(url || null);
              setFreeRunRetry(false);
              setScannerJob({ jobId: String(jobId), brandImage: brandProfilePicture, title: "Your stream", videoUrl: url || null });
              setScannerOpen(true);
            }}
            onSkip={() => setFreeRunRetry(false)}
          />
        </div>
      </div>
    );
  }
  if (forcePaywall) {
    return <HardPaywall readyClips={freeRunClipsSoFar} onClose={() => setForcePaywall(false)} />;
  }
  if (needsPaywall && !paywallDismissed) {
    // Closing (X) runs the free-gift flow first; "Still leave" lands on the
    // locked home screen, and any tap there reopens the paywall.
    // After a free run, leaving the paywall lands on their clips (playable, locked).
    return <HardPaywall readyClips={freeRunClips} onClose={() => { setPaywallDismissed(true); if (freeRunClips.length) navigate("/home?tab=library"); }} />;
  }

  // Don't render until we've checked onboarding status (and, for unpaid
  // accounts, loaded jobs once — a try run in progress decides what shows).
  if (!onboardingChecked || (!jobsLoadedOnce && userPlan === "none")) {
    return (
      <div className="foryou-page">
        <div className="foryou-loading-screen">
          <img src="/favicon.png" alt="Clipbait" className="foryou-loading-logo" />
        </div>
      </div>
    );
  }

  // Hide banner if user has a plan OR has purchased credits
  const isFreeTrial = (userPlan === "none" || userPlan === null) && purchasedCredits === 0;
  // A user already on a paid plan should never see the OWN "Go Pro + bonus"
  // upsell — they've gone Pro. (The bonus also grants on payment, flipping the
  // status off "pending", but gate on plan too as a safety net.)
  const isPaidPlan = !!userPlan && userPlan !== "none";
  const showOwnPendingBanner = referralBonusStatus === "pending" && !isPaidPlan;
  const showFreeBanner =
    isFreeTrial && userPlan !== null && referralBonusStatus !== "pending" && !freeBannerDismissed && !isNarrow;

  return (
    <div
      className={`foryou-page${(showOwnPendingBanner || showFreeBanner) ? " has-trial-banner" : ""}${activeTab === "home" ? " foryou-home-locked" : ""}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* OWN partner pending banner — bonus unlocks on first paid plan. Takes
          priority over the generic free-trial banner. Hidden once granted. */}
      {showOwnPendingBanner && (
        <div className="own-pending-banner">
          <span className="own-pending-banner-text">
            <FiGift /> Go Pro and unlock 150 bonus credits. Your first stream, on us. Credits never expire.
          </span>
          <button className="own-pending-banner-btn" onClick={() => openUpgrade()}>
            {t('common:upgrade')}
          </button>
        </div>
      )}

      {/* Free Trial Banner */}
      {showFreeBanner && (
        <div className="free-trial-banner">
          <span className="free-trial-banner-text">{t('freeTrialBanner')}</span>
          <button className="free-trial-banner-btn" onClick={() => openUpgrade()}>
            {t('common:upgrade')}
          </button>
          {/* Closing a banner is harmless, so it's exempt from the paywall lock. */}
          <button
            type="button"
            className="free-trial-banner-close"
            onClick={dismissFreeBanner}
            aria-label="Dismiss"
            data-paywall-allow
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Phone number prompt for SMS clip-ready notifications */}
      {showPhonePrompt && (
        <div className="phone-prompt-overlay" onClick={skipPhonePrompt}>
          <div className="phone-prompt-modal" onClick={(e) => e.stopPropagation()}>
            {/* Visual first: what the text will look like on their lock screen. */}
            <div className="phone-prompt-preview" aria-hidden="true">
              <div className="ppv-notif">
                <img src="/mascot.png" alt="" className="ppv-icon" />
                <div className="ppv-body">
                  <div className="ppv-top"><b>Clipbait</b><span>now</span></div>
                  <div className="ppv-msg"><FiZap /> 8 new clips are ready to post</div>
                </div>
              </div>
              <div className="ppv-notif ppv-notif--back" />
            </div>
            <h2 className="phone-prompt-title">Get a text when clips are ready</h2>
            <input
              type="tel"
              className="phone-prompt-input"
              placeholder="Your phone number"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !phoneSaving && smsConsent && savePhoneNumber()}
              autoFocus
            />

            {/* Explicit, optional SMS consent — TCPA / A2P 10DLC compliant.
                Unchecked by default, stands alone, required only to save the
                phone (Skip uses the product without it). The full required
                disclosure (SMS_CONSENT_LANGUAGE) stays next to the box as
                fine print and is what we store as the consent record. */}
            <label className="phone-prompt-consent">
              <input
                type="checkbox"
                className="phone-prompt-consent-box"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
              />
              <span className="phone-prompt-consent-label">Text me clip alerts &amp; offers</span>
            </label>
            <p className="phone-prompt-fine">{SMS_CONSENT_LANGUAGE}</p>

            <div className="phone-prompt-actions">
              <button
                type="button"
                className="phone-prompt-save"
                onClick={savePhoneNumber}
                disabled={phoneSaving || !phoneInput.trim() || !smsConsent}
              >
                {phoneSaving ? "Saving…" : freeRunRunning ? "Save" : "Save & generate"}
              </button>
              <button
                type="button"
                className="phone-prompt-skip"
                onClick={skipPhonePrompt}
                disabled={phoneSaving}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing popup — the SaaS paywall shown when an unpaid user hits a
          gated action (Generate / Upload) or taps Upgrade. */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleUpgradeClose}
        initialTab={upgradeTab}
        ownOffer={referralBonusStatus === "pending"}
        message={
          referralBonusStatus === "pending"
            ? "Go Pro and we'll drop 150 bonus credits into your account instantly. That's your first stream on us. They never expire."
            : "Upgrade to start generating clips."
        }
        currentPlan={userPlan}
        currentInterval={userPlanInterval}
      />

      {/* Credits empty popup */}
      {showCreditsPopup && (
        <div className="credits-popup">
          <div className="credits-popup-text">
            <span className="credits-popup-bold">You&apos;re out of credits.</span>
            {" "}Top up to keep clipping.
          </div>
          <button
            className="credits-popup-upgrade"
            onClick={() => { setShowCreditsPopup(false); openUpgrade("credits"); }}
          >
            Buy credits
          </button>
          <button className="credits-popup-close" onClick={() => setShowCreditsPopup(false)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      <Topbar
        creditRemaining={generationsRemaining}
        creditTotal={generationsTotal}
        nextExpiringGrant={nextExpiringGrant}
        activeGrants={activeGrants}
        onUpgrade={openUpgrade}
        userPlan={userPlan}
      />
      <div className="foryou-leftbar-wrapper"><Leftbar /></div>
      <div className="foryou-bg-gradient" />

      {/* HOME — always the mascot hero (never the clips grid). The generate
          input lives in the floating bottom bar, revealed by "Paste A Link". */}
      <div className="foryou-home-area" style={activeTab !== "home" ? { display: "none" } : {}}>
        <div className="foryou-hero">
          <div className="foryou-hero-bubble">
            {homeLocked
              ? <>{tp("lock_bubble_1")}<br />{tp("lock_bubble_2")}</>
              : <>Paste your link or connect your channel,<br />I'll do the clipping.</>}
          </div>
          <div className="foryou-hero-mascot">
            <img src="/mascot.png" alt="Clipbait" onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = "/favicon.png"; } }} />
          </div>
          <div className="foryou-hero-actions">
            {/* Connect Channel — PRIMARY. Promotes the fully-automatic path:
                connect once, we auto-clip every stream. */}
            {(() => {
              // Show whichever channel is connected (Twitch / Kick / YouTube), else the CTA.
              const conn = twitchConn?.connected
                ? { ...twitchConn, label: "Twitch connected", Icon: SiTwitch, color: "#9146FF" }
                : kickConn?.connected
                ? { ...kickConn, label: "Kick connected", Icon: SiKick, color: "#53FC18" }
                : youtubeConn?.connected
                ? { ...youtubeConn, label: "YouTube connected", Icon: SiYoutube, color: "#FF0000" }
                : null;
              if (!conn && homeLocked) {
                return (
                  <button className="foryou-hero-btn foryou-hero-btn--primary foryou-hero-btn--locked">
                    <svg className="foryou-lock-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7.5a4 4 0 0 1 8 0V11" /></svg>
                    <span className="foryou-lock-text">
                      <b>{tp("lock_live")}</b>
                      <small>{tp("lock_live_sub")}</small>
                    </span>
                  </button>
                );
              }
              if (!conn) {
                return (
                  <button
                    className="foryou-hero-btn foryou-hero-btn--primary"
                    onClick={() => { setLiveModalUrl(""); setLiveModalOpen(true); }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                      <polygon points="6 4 20 12 6 20 6 4" />
                    </svg>
                    Connect Channel
                  </button>
                );
              }
              const { Icon } = conn;
              const glyph = conn.color === "#53FC18" ? "#0a0a0b" : "#fff";
              return (
                <button
                  className="foryou-hero-btn foryou-hero-btn--connected"
                  onClick={() => { setLiveModalUrl(""); setLiveModalOpen(true); }}
                  title="Manage your connected channel"
                >
                  <span className="foryou-conn-icon" style={{ background: conn.color }}>
                    <Icon size={24} color={glyph} />
                  </span>
                  <span className="foryou-conn-mid">
                    <b className="foryou-conn-title">{conn.label}</b>
                    <span className="foryou-conn-sub">
                      <span className="foryou-conn-handle">@{conn.username}</span>
                      <span className="foryou-conn-dot">·</span>
                      {homeLocked ? (
                        <span className="foryou-conn-live-text foryou-conn-locked">{tp("lock_conn")}</span>
                      ) : (
                        <>
                          <span className="foryou-conn-live"><span className="foryou-conn-ping" /><span className="foryou-conn-live-dot" /></span>
                          <span className="foryou-conn-live-text">auto-clipping on</span>
                        </>
                      )}
                    </span>
                  </span>
                  <span className="foryou-conn-check">
                    {homeLocked
                      ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ff8a3d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7.5a4 4 0 0 1 8 0V11" /></svg>
                      : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ff8a3d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  </span>
                </button>
              );
            })()}
            {/* Paste A Link — SECONDARY (manual one-off clip). */}
            {homeLocked ? (
              <button className="foryou-hero-btn foryou-hero-btn--ghost foryou-hero-btn--locked">
                <svg className="foryou-lock-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7.5a4 4 0 0 1 8 0V11" /></svg>
                <span className="foryou-lock-text">
                  <b>{tp("lock_clips")}</b>
                  <small>{tp("lock_clips_sub")}</small>
                </span>
              </button>
            ) : (
            <button
              className="foryou-hero-btn foryou-hero-btn--ghost"
              onClick={() => { setInputMode("url"); setComposerOpen(true); }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Paste A Link
            </button>
            )}
            {homeLocked && (
              <p className="foryou-lock-note">
                {homeGiftPrice ? tp("lock_note_gift", { price: money(homeGiftPrice) }) : tp("lock_note", { price: money(lockedPrice) })}
                {homeGiftLeft > 0 && (
                  <><br />{tp("ends_in")} <span className="foryou-lock-countdown">{formatLeft(homeGiftLeft)}</span></>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* LIBRARY — every clip (live, processing, finished). Tap a finished clip
          to open the full-screen swipe viewer. */}
      <div className="foryou-main-area" style={activeTab !== "library" ? { display: "none" } : {}}>

        {/* Demo mode control — admin account only, hidden while a demo runs so
            it never shows up in a screen recording (Shift+D stops a demo). */}
        {isDemoAdmin && !demoLive && (
          <div className="foryou-demo">
            {!demoPanelOpen ? (
              <button type="button" className="foryou-demo-toggle" onClick={() => setDemoPanelOpen(true)}>
                <FiFilm /> Demo mode
              </button>
            ) : (
              <DemoPanel
                jobs={jobs.filter((j) => j.status === "completed" && (j.clips?.length || 0) > 0).sort(byNewest)}
                onCancel={() => setDemoPanelOpen(false)}
                onStart={(jobId, intervalSec) => {
                  setDemoLive({ jobId, revealed: 0, intervalSec });
                  setDemoPanelOpen(false);
                  setLiveDismissed(false);
                  setLiveScannerOpen(true);
                  toast("Demo running — press Shift+D to stop", { duration: 2500 });
                }}
              />
            )}
          </div>
        )}

        {/* Clips grid */}
        <div className="foryou-clips-grid">
          {/* Live auto-clipping status card — shows while a session is running.
              Tap to manage/stop; clips it produces appear as normal cards. */}
          {shownLiveSession && (
            <div
              className="foryou-clip-card foryou-clip-card--processing foryou-clip-card--live"
              onClick={() => { setLiveDismissed(false); setLiveScannerOpen(true); }}
            >
              {shownLiveSession.videoThumbnail && (
                <img className="foryou-clip-skeleton-thumb" src={shownLiveSession.videoThumbnail} alt="" draggable={false} />
              )}
              <div className="foryou-clip-skeleton-vignette" />
              {/* Indeterminate orange sweep — the stream is ongoing (no fixed
                  progress), so a continuous left-to-right band keeps it active. */}
              <div className="foryou-clip-skeleton-fill foryou-clip-skeleton-fill--live" />
              <div className="foryou-clip-card-processing-inner">
                <span className="foryou-live-badge"><span className="foryou-live-dot" />LIVE</span>
                <span className="foryou-clip-card-processing-label">Live auto clipping</span>
                <span className="foryou-clip-card-processing-title">
                  {(shownLiveSession.clips?.length || 0)} clip{shownLiveSession.clips?.length === 1 ? "" : "s"} generated
                </span>
                <button
                  className="foryou-clip-card-cancel-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLiveDismissed(false);
                    setLiveScannerOpen(true);
                  }}
                >
                  Manage
                </button>
              </div>
            </div>
          )}
          {/* Processing jobs as skeleton cards — one card per expected clip */}
          {activeJobs.flatMap(job =>
            // Placeholders for clips still coming — a few, not one per possible
            // clip (Auto asks for up to 20 and most jobs ship far fewer).
            Array.from({ length: Math.max(0, Math.min(3, (job.maxClips || 3) - (job.clips?.length || 0))) }, (_, i) => (
              <div
                key={`${job._id}-skeleton-${i}`}
                className="foryou-clip-card foryou-clip-card--processing"
                onClick={() => handleJobTap(job)}
              >
                {job.videoThumbnail && (
                  <img
                    className="foryou-clip-skeleton-thumb"
                    src={job.videoThumbnail}
                    alt=""
                    draggable={false}
                  />
                )}
                <div className="foryou-clip-skeleton-vignette" />
                {/* Left-to-right transparent orange progress fill —
                    width tracks job.progress so users see the bar
                    crawl across each card as the worker advances. */}
                <div
                  className="foryou-clip-skeleton-fill"
                  style={{ width: `${Math.max(2, Math.min(100, job.progress || 0))}%` }}
                />
                <div className="foryou-clip-card-processing-inner">
                  <span className="foryou-spinner-small" />
                  <span className="foryou-clip-card-processing-label">
                    {job.progress > 0 ? `${job.progress}%` : t('processingStatus')}
                    {typeof job.etaSeconds === "number" && job.etaSeconds > 0 && (
                      <span className="foryou-clip-card-processing-eta">
                        {" · "}
                        {job.etaSeconds < 60
                          ? t('underTwoMinutes')
                          : t('minutesRemaining', { count: Math.ceil(job.etaSeconds / 60) })}
                      </span>
                    )}
                  </span>
                  {(getVideoTitle(job) || job.streamerName) && (
                    <span className="foryou-clip-card-processing-title">
                      {getVideoTitle(job) || job.streamerName}
                    </span>
                  )}
                  {i === 0 && (
                    <button
                      className="foryou-clip-card-cancel-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteJob(job); }}
                    >
                      {t('cancelJobBtn')}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Failed jobs — hidden for now (SHOW_FAILED_JOBS); they clutter the Library. */}
          {SHOW_FAILED_JOBS && failedJobs.map(job => (
            <div key={job._id} className="foryou-clip-card foryou-clip-card--failed">
              <div className="foryou-clip-card-failed-badges">
                <span className="foryou-clip-badge foryou-clip-badge--failed">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  {t('failed')}
                </span>
                <span className="foryou-clip-badge foryou-clip-badge--refunded">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {t('creditsRefunded')}
                </span>
              </div>
              <div>
                <div className="foryou-clip-card-failed-msg">
                  {/* Map the worker's failure reason to friendly copy. A
                      zero-segment transcription means the pasted video had no
                      spoken words (e.g. music/silent footage). */}
                  {/zero segments|no\s*(speech|words|utterances|transcript)|transcription produced/i.test(
                    job.error || "",
                  )
                    ? t('noSpeechInVideo')
                    : t('tryAgainDifferentLink')}
                </div>
                {(getVideoTitle(job) || job.streamerName) && (
                  <div className="foryou-clip-card-failed-title">{getVideoTitle(job) || job.streamerName}</div>
                )}
              </div>
              <div className="foryou-clip-card-failed-actions">
                {job.videoUrl && (
                  <button
                    className="foryou-clip-card-failed-btn foryou-clip-card-failed-btn--retry"
                    onClick={() => handleRetryJob(job)}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.1"/>
                    </svg>
                    {t('retry')}
                  </button>
                )}
                <button
                  className="foryou-clip-card-failed-btn foryou-clip-card-failed-btn--delete"
                  onClick={() => handleDeleteFailedJob(job._id)}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  {t('common:delete')}
                </button>
              </div>
            </div>
          ))}

          {/* Completed clips */}
          {allClips.map(({ clip, job, clipIndex }, idx) => {
            const clipKey = `${job._id}-${clipIndex}`;
            const isSelected = selectedClipIds.includes(clipKey);
            const isLiked = likedClipIds.includes(clipKey);

            // "Expires in X days" countdown (Opus-style) from the job's
            // retention window. Only shown once expiresAt is set.
            const daysLeft = job.expiresAt
              ? Math.ceil((new Date(job.expiresAt).getTime() - Date.now()) / 86400000)
              : null;
            const expiryLabel =
              daysLeft == null
                ? null
                : daysLeft <= 0
                  ? t('expiresToday')
                  : daysLeft === 1
                    ? t('expiresTomorrow')
                    : t('expiresInDays', { count: daysLeft });

            // Coach Mode: render the MomentCard (guidance + music + score)
            // instead of the video card. Each moment spans 2 grid cols on
            // wide screens so the coaching text breathes.
            if (job.mode === "coach") {
              return (
                <div
                  key={clip._id || `${job._id}-${idx}`}
                  className="foryou-moment-cell"
                  style={{ animationDelay: `${Math.min(idx, 15) * 0.04}s` }}
                >
                  <MomentCard
                    clip={clip}
                    index={clipIndex}
                    job={job}
                    isSelected={isSelected}
                    isLiked={isLiked}
                    onOpen={handleClipClick}
                    onToggleSelect={(e) => toggleSelect(clipKey, e)}
                    onToggleLike={(e) => toggleLike(clipKey, e)}
                    onDelete={(e) => handleDeleteClip(clip, job, clipIndex, e)}
                  />
                </div>
              );
            }

            return (
              <div
                key={clip._id || `${job._id}-${idx}`}
                className={`foryou-clip-card${isSelected ? ' selected' : ''}${job.aspectRatio === '16:9' ? ' foryou-clip-card--wide' : ''}`}
                style={{ animationDelay: `${Math.min(idx, 15) * 0.04}s` }}
                onClick={() => setViewerStart(idx)}
                onMouseEnter={(e) => { const v = e.currentTarget.querySelector('.foryou-clip-card-video'); if (v) v.play().catch(() => {}); }}
                onMouseLeave={(e) => { const v = e.currentTarget.querySelector('.foryou-clip-card-video'); if (v) { v.pause(); v.currentTime = 0; } }}
              >
                {clip.thumbnailUrl && (
                  <img
                    className="foryou-clip-thumb"
                    src={`/api/clips/thumb?u=${encodeURIComponent(clip.thumbnailUrl)}`}
                    alt=""
                    aria-hidden="true"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <video
                  className="foryou-clip-card-video"
                  src={clip.editedUrl || clip.url}
                  preload="metadata"
                  muted
                  playsInline
                  loop
                  crossOrigin="anonymous"
                  onLoadedMetadata={e => { if (!clip.thumbnailUrl) e.currentTarget.currentTime = 0.001; }}
                />

                {/* Top-left: select checkbox */}
                <button className="foryou-clip-select-btn" onClick={(e) => toggleSelect(clipKey, e)}>
                  <div className={`foryou-clip-checkbox${isSelected ? ' checked' : ''}`}>
                    {isSelected && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </button>

                {/* Right side: like + download */}
                <div className="foryou-clip-side-actions">
                  <button
                    className={`foryou-clip-action-btn${isLiked ? ' liked' : ''}`}
                    onClick={(e) => toggleLike(clipKey, e)}
                    title="Like"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24"
                      fill={isLiked ? "currentColor" : "none"}
                      stroke="currentColor" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                  </button>
                  <button
                    className="foryou-clip-action-btn"
                    onClick={requirePro((e) => handleQuickDownload(clip, job, clipIndex, e))}
                    title={homeLocked ? "Unlock Pro to download" : "Download"}
                  >
                    {homeLocked ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="11" width="16" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    )}
                  </button>
                  <button
                    className="foryou-clip-action-btn foryou-clip-action-btn--delete"
                    onClick={(e) => handleDeleteClip(clip, job, clipIndex, e)}
                    title="Delete clip"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>

                {expiryLabel && (
                  <div
                    className={`foryou-clip-expiry${daysLeft <= 1 ? ' foryou-clip-expiry--soon' : ''}`}
                    title="Clips are auto-deleted after this. Download to keep."
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <polyline points="12 7 12 12 15 14" />
                    </svg>
                    {expiryLabel}
                  </div>
                )}

                <div className="foryou-clip-card-overlay" />
                {homeLocked && (
                  <div className="foryou-clip-locked" aria-label="Unlock Pro to watch">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  </div>
                )}
              </div>
            );
          })}

          {/* Loading state */}
          {jobsLoading && allClips.length === 0 && activeJobs.length === 0 && (
            <div className="foryou-clips-empty">
              <span className="foryou-spinner-small" />
            </div>
          )}

          {/* Empty state — nothing clipped yet. The home tab is where you start. */}
          {!jobsLoading && allClips.length === 0 && activeJobs.length === 0 && (
            <div className="foryou-clips-empty">
              <div className="foryou-empty-icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="7" height="7" rx="1.5" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
              </div>
              <h3>No clips yet</h3>
              <p>Head to Home and paste a link to make your first clip.</p>
            </div>
          )}
        </div>
      </div>

      {/* Floating Bottom Bar — Higgsfield style. Home tab only, and on the clean
          first-run hero it stays hidden until "Paste A Link" opens it (Sherlock
          simplicity). Once there are clips/jobs it's always available. */}
      {composerOpen && (
        <div className="mkc-overlay" onClick={() => setComposerOpen(false)}>
          <div className="mkc-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mkc-head">
              <h3 className="mkc-title">{firstClipPick ? "Pick how many minutes to clip" : "Make clips"}</h3>
              <button className="mkc-close" onClick={() => setComposerOpen(false)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
              </button>
            </div>

            {/* Source: paste a link or switch to file upload */}
            <div className="mkc-source">
              {inputMode === "url" ? (
                <input
                  ref={urlInputRef}
                  type="text"
                  className="mkc-input"
                  placeholder={placeholders[placeholderIndex]}
                  value={videoUrl}
                  onChange={handleUrlChange}
                  onPaste={handlePaste}
                />
              ) : selectedFile ? (
                <div className="mkc-file">
                  <span className="mkc-file-name">{selectedFile.name}</span>
                  <button className="mkc-file-clear" onClick={() => setSelectedFile(null)} aria-label="Remove file">&times;</button>
                </div>
              ) : (
                <label className="mkc-file-label">
                  <input type="file" accept=".mp4,.mov" onChange={handleFileSelect} style={{ display: "none" }} />
                  {isDragging ? t('dropFileHere') : t('mp4OrMovMax2GB')}
                </label>
              )}
              <button
                className="mkc-source-btn"
                onClick={() => setInputMode(m => m === "url" ? "upload" : "url")}
                title={inputMode === "url" ? t('switchToFileUpload') : t('switchToURL')}
                aria-label={inputMode === "url" ? t('switchToFileUpload') : t('switchToURL')}
              >
                {inputMode === "url" ? (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                )}
              </button>
            </div>

            {/* Aspect ratio — preview cards */}
            <div className="mkc-aspect">
              <button type="button" className={`mkc-aspect-card${aspectRatio === "9:16" ? " on" : ""}`} onClick={() => setAspectRatio("9:16")}>
                <span className="mkc-shape mkc-shape--v" />
                <span className="mkc-aspect-lbl">9:16</span>
                <span className="mkc-aspect-sub">Vertical</span>
              </button>
              <button type="button" className={`mkc-aspect-card${aspectRatio === "16:9" ? " on" : ""}`} onClick={() => setAspectRatio("16:9")}>
                <span className="mkc-shape mkc-shape--w" />
                <span className="mkc-aspect-lbl">16:9</span>
                <span className="mkc-aspect-sub">Wide</span>
              </button>
            </div>

            {/* Content type + clip count dropdowns (clip count hidden: SHOW_CLIP_COUNT) */}
            <div className="mkc-dds">
              <div className="mkc-dd">
                <button type="button" className="mkc-dd-btn" onClick={() => { setTypeMenuOpen(o => !o); setClipCountOpen(false); }}>
                  <span>{t(CONTENT_TYPES.find(ct => ct.value === contentType)?.labelKey) || t('type')}</span>
                  <svg className="mkc-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {typeMenuOpen && (
                  <>
                    <div className="mkc-dd-backdrop" onClick={() => setTypeMenuOpen(false)} />
                    <div className="mkc-dd-menu">
                      {CONTENT_TYPES.map(ct => (
                        <button key={ct.value} type="button" className={`mkc-dd-item${contentType === ct.value ? " on" : ""}`} onClick={() => { setContentType(ct.value); setTypeMenuOpen(false); }}>{t(ct.labelKey)}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {SHOW_CLIP_COUNT && <div className="mkc-dd">
                <button type="button" className="mkc-dd-btn" onClick={() => { setClipCountOpen(o => !o); setTypeMenuOpen(false); }}>
                  <span>{clipCount === "auto" ? "Auto" : `${clipCount} clips`}</span>
                  <svg className="mkc-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {clipCountOpen && (
                  <>
                    <div className="mkc-dd-backdrop" onClick={() => setClipCountOpen(false)} />
                    <div className="mkc-dd-menu">
                      {["auto", 1, 2, 3, 5, 8, 10, 15, 20].map(opt => (
                        <button key={opt} type="button" className={`mkc-dd-item${clipCount === opt ? " on" : ""}`} onClick={() => { setClipCount(opt); setClipCountOpen(false); }}>{opt === "auto" ? "Auto" : `${opt} clips`}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>}
            </div>

            {/* Add-ons — tap-to-fill chips */}
            {showBurnToggles && (
              <div className="mkc-addons">
                {titleAndSubsAvailable && (
                  <button type="button" className={`mkc-chip${burnHookTitle ? " on" : ""}`} onClick={() => setBurnHookTitle(v => !v)}>
                    <span className="mkc-chip-ic">{burnHookTitle ? "✓" : "+"}</span>Title captions
                  </button>
                )}
                {titleAndSubsAvailable && (
                  <button type="button" className={`mkc-chip${burnSubtitles ? " on" : ""}`} onClick={() => setBurnSubtitles(v => !v)}>
                    <span className="mkc-chip-ic">{burnSubtitles ? "✓" : "+"}</span>Subtitles
                  </button>
                )}
                <button type="button" className={`mkc-chip${burnWatermark ? " on" : ""}`} onClick={() => setBurnWatermark(v => !v)}>
                  <span className="mkc-chip-ic">{burnWatermark ? "✓" : "+"}</span>Channel watermark
                </button>
              </div>
            )}

            {/* Clip length — "Detecting…" then the trimmer. Renders nothing
                without a VOD duration. Sits between add-ons and Generate. */}
            {!detectedDurationLoading && Number.isFinite(detectedDuration) && detectedDuration > 0 && <div className="mkc-cl-divider" />}
            <ClipLengthTrimmer
              loading={detectedDurationLoading}
              duration={detectedDuration}
              start={rangeStart}
              end={rangeEnd}
              onChange={(st, en) => { setRangeStart(st); setRangeEnd(en); }}
            />

            {/* Generate — credits count sits on the button, left of the bolt */}
            {(() => {
              const finite = Number.isFinite(detectedDuration) && detectedDuration > 0;
              const selSec = finite ? Math.max(1, (rangeEnd ?? detectedDuration) - (rangeStart ?? 0)) : null;
              const credits = selSec != null
                ? (genMode === "coach" ? 1 : Math.max(1, Math.round(selSec / 60)))
                : null;
              return (
                <button
                  className="mkc-generate"
                  onClick={() => withPhoneCheck(() => inputMode === "url" ? handleGenerate() : handleUpload())}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="mkc-spinner" />
                  ) : (
                    <>
                      <span>{genMode === "coach" ? "Find Moments" : "Generate"}</span>
                      {credits != null && <span className="mkc-gen-credits">{credits}</span>}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                    </>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      )}


      {/* Single-clip detail panel — picks Coach (ClipFinder) or Streamer
          (Clipper) view based on the clicked job's mode. Both panels
          receive global prev/next handlers that walk `allClips` across
          ALL jobs regardless of mode, so the arrows never hit a wall
          when transitioning from ClipFinder → Clipper or vice-versa. */}
      {selectedClipDetail && (() => {
        const flatIndex = allClips.findIndex(({ job, clipIndex }) =>
          job._id === selectedClipDetail.job._id && clipIndex === selectedClipDetail.clipIndex
        );
        const hasPrev = flatIndex > 0;
        const hasNext = flatIndex >= 0 && flatIndex < allClips.length - 1;
        const stepGlobal = (delta) => {
          if (flatIndex < 0) return;
          const next = allClips[flatIndex + delta];
          if (next) setSelectedClipDetail(next);
        };

        if (selectedClipDetail.job?.mode === "coach") {
          const { clip, job, clipIndex } = selectedClipDetail;
          return (
            <CoachingPanel
              clip={clip}
              index={clipIndex}
              job={job}
              catalog={musicCatalog}
              open={true}
              onClose={() => setSelectedClipDetail(null)}
              onPrev={() => stepGlobal(-1)}
              onNext={() => stepGlobal(1)}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onDelete={(e) => handleDeleteClip(clip, job, clipIndex, e)}
            />
          );
        }

        return (
        <ClipDetailPanel
          clip={selectedClipDetail.clip}
          job={selectedClipDetail.job}
          clipIndex={selectedClipDetail.clipIndex}
          userPlan={userPlan}
          catalog={musicCatalog}
          onPrev={() => stepGlobal(-1)}
          onNext={() => stepGlobal(1)}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onClose={() => setSelectedClipDetail(null)}
          onUpgrade={() => { setSelectedClipDetail(null); openUpgrade(); }}
          onDeleteClip={() => {
            if (selectedClipDetail) {
              const { clip, job, clipIndex } = selectedClipDetail;
              if ((job.clips || []).length <= 1) {
                setSelectedClipDetail(null);
                setJobToDelete(job);
                setShowDeleteModal(true);
              } else {
                setJobs(prev => prev.map(j => {
                  if (j._id !== job._id) return j;
                  const newClips = [...(j.clips || [])];
                  newClips.splice(clipIndex, 1);
                  return { ...j, clips: newClips };
                }));
                setSelectedClipDetail(null);
                toast.success("Clip removed");
              }
            }
          }}
        />
        );
      })()}

      {/* Progress Modal */}
      {showProgressModal && selectedJob && (
        <JobProgressModal
          job={selectedJob}
          videoTitle={getVideoTitle(selectedJob)}
          videoThumbnail={getVideoThumb(selectedJob)}
          videoAuthor={selectedJob.platform === "rumble" ? selectedJob.videoAuthor : null}
          videoDuration={selectedJob.platform === "rumble" ? formatDuration(selectedJob.videoDuration) : null}
          onClose={closeModals}
        />
      )}

      {/* Clips Modal */}
      {showClipsModal && selectedJob && (
        <ClipsModal
          job={selectedJob}
          videoTitle={getVideoTitle(selectedJob)}
          videoThumbnail={getVideoThumb(selectedJob)}
          videoAuthor={selectedJob.platform === "rumble" ? selectedJob.videoAuthor : null}
          onClose={closeModals}
          userPlan={userPlan}
          onUpgrade={() => { closeModals(); openUpgrade(); }}
          onGenerateAnother={handleGenerateAnother}
        />
      )}

      {/* Library bulk-select action bar — Download all / Delete when clips are ticked */}
      {activeTab === "library" && selectedClipIds.length > 0 && (
        <div className="foryou-libsel-bar">
          <span className="foryou-libsel-count">{selectedClipIds.length} selected</span>
          <div className="foryou-libsel-actions">
            <button className="foryou-libsel-btn" onClick={requirePro(handleBulkDownload)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download
            </button>
            <button className="foryou-libsel-btn foryou-libsel-btn--danger" onClick={handleBulkDelete}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Delete
            </button>
          </div>
          <button className="foryou-libsel-clear" onClick={() => setSelectedClipIds([])}>Cancel</button>
        </div>
      )}

      {/* Full-screen TikTok-style clip viewer — tap any finished clip to open */}
      {viewerStart !== null && (
        <ClipFeedViewer
          clips={freeRunReveal ? (freeRunJob?.clips || []).map((clip, clipIndex) => ({ clip, job: freeRunJob, clipIndex })).filter((x) => x.clip.url) : allClips}
          startIndex={viewerStart}
          likedIds={likedClipIds}
          catalog={musicCatalog}
          onClose={() => (freeRunReveal ? endFreeRunReveal() : setViewerStart(null))}
          onLike={(key) => toggleLike(key, { stopPropagation() {} })}
          locked={homeLocked || freeRunReveal}
          onLocked={freeRunReveal ? freeRunLockTap : requirePro(() => {})}
          onDownload={requirePro((clip, job, clipIndex, e) => handleQuickDownload(clip, job, clipIndex, e))}
          onDelete={(clip, job, clipIndex, e) => handleDeleteClip(clip, job, clipIndex, e)}
          onPublish={requirePro((clip, job, clipIndex) => setPublishTarget({ clip, job, clipIndex }))}
        />
      )}

      {/* Publish / schedule popup — opened from the viewer's Post button. Renders
          above the viewer (z 10001). */}
      {publishTarget && (
        <ScheduleModal
          clip={publishTarget.clip}
          clipJobId={publishTarget.job._id}
          clipIndex={publishTarget.clipIndex}
          onClose={() => setPublishTarget(null)}
          onScheduled={() => setPublishTarget(null)}
          initialAction="schedule"
        />
      )}

      {/* Sherlock-style clipping scanner. Closing minimizes it (the job keeps
          running); it re-opens from the pill and is dismissed once the results
          are opened in Library. */}
      {scannerJob && scannerOpen && (
        <ClippingScanner
          brandImage={(scannerJob.jobId === freeRunId ? brandProfilePicture : null) || scannerJob.brandImage || brandProfilePicture || twitchConn?.avatar || kickConn?.avatar || youtubeConn?.avatar}
          title={scannerJob.title}
          progress={scannerProgress}
          resultsCount={scannerResults}
          done={scannerDone}
          failed={scannerFailed}
          videoUrl={trackedJob?.videoUrl || scannerJob.videoUrl}
          etaSeconds={trackedJob?.etaSeconds}
          status={trackedJob?.status}
          currentStep={trackedJob?.currentStep}
          inFlow={freeRunLocked && !!scannerJob.jobId && scannerJob.jobId === freeRunId}
          onRetry={scannerJob.jobId && scannerJob.jobId === freeRunId ? () => { setScannerOpen(false); setScannerJob(null); setFreeRunRetry(true); } : undefined}
          onClose={() => setScannerOpen(false)}
          onViewClips={freeRunLocked && scannerJob.jobId && scannerJob.jobId === freeRunId
            ? openFreeRunReveal
            : () => { setScannerOpen(false); setActiveTab("library"); }}
          // The free "try it" run can't be cancelled (it's their one free run).
          onCancel={scannerJob.jobId && scannerJob.jobId === freeRunId ? undefined : async () => {
            const id = scannerJob.jobId;
            const wasDone = scannerDone;
            setScannerJob(null);
            setScannerOpen(false);
            if (id && !wasDone) {
              try {
                await axiosInstance.delete(`/clips/jobs/${id}`);
                setJobs(prev => prev.filter(j => j._id !== id));
              } catch { /* ignore */ }
            }
          }}
        />
      )}

      {/* Minimized pill — re-open the scanner while a run is in progress */}
      {scannerJob && !scannerOpen && (
        <button className="foryou-scanner-pill" onClick={() => setScannerOpen(true)} data-paywall-allow={homeLocked ? "" : undefined}>
          {scannerDone ? (
            <><span className="foryou-scanner-pill-dot foryou-scanner-pill-dot--done" />Clips ready</>
          ) : (
            <><span className="foryou-scanner-pill-spin" />Clipping {Math.round(scannerProgress)}%</>
          )}
        </button>
      )}

      {/* AUTO / LIVE session — same Sherlock scanner, live mode. Shows whenever a
          live clipping session is running (e.g. Twitch went live). */}
      {shownLiveSession && liveScannerOpen && !scannerJob && (
        <ClippingScanner
          live
          platform={shownLiveSession.platform}
          brandImage={shownLiveSession.creatorImageUrl || twitchConn?.avatar || kickConn?.avatar || youtubeConn?.avatar || shownLiveSession.videoThumbnail || brandProfilePicture}
          title={shownLiveSession.videoTitle || shownLiveSession.streamerName || "Your stream"}
          resultsCount={shownLiveSession.clips?.length || 0}
          onClose={() => { setLiveScannerOpen(false); setLiveDismissed(true); }}
          onViewClips={() => { setLiveScannerOpen(false); setLiveDismissed(true); setActiveTab("library"); }}
          onCancel={() => {
            if (shownLiveSession.isDemo) { stopDemo(); return; }
            const id = String(shownLiveSession._id);
            // Optimistic: kill the scanner + pill instantly, block a poll from
            // re-adding this session, then stop it server-side in the background.
            stoppedLiveIdRef.current = id;
            dismissedEndedIdRef.current = id; // user stopped on purpose — no "ended" card
            setLiveScannerOpen(false); setLiveDismissed(true);
            setActiveLiveSession(null);
            axiosInstance.post(`/live/${id}/stop`).catch(() => {}).finally(() => refreshActiveLive());
          }}
        />
      )}
      {shownLiveSession && !liveScannerOpen && !scannerJob && (
        <button className="foryou-scanner-pill" onClick={() => { setLiveScannerOpen(true); setLiveDismissed(false); }}>
          <span className="foryou-scanner-pill-dot" style={{ background: "#ff3b30", boxShadow: "0 0 8px #ff3b30" }} />
          Live · {shownLiveSession.clips?.length || 0} clips
        </button>
      )}

      {/* Just-ended live session — "done" state that keeps the Sherlock page +
          clips + reason (out of credits / stream ended) visible until the user
          views or closes it, instead of the scanner silently disappearing. */}
      {endedLiveSession && !activeLiveSession && !scannerJob && (
        <ClippingScanner
          live
          ended
          endedReason={endedLiveSession.endedReason}
          platform={endedLiveSession.platform}
          brandImage={endedLiveSession.creatorImageUrl || twitchConn?.avatar || kickConn?.avatar || youtubeConn?.avatar || endedLiveSession.videoThumbnail || brandProfilePicture}
          title={endedLiveSession.videoTitle || endedLiveSession.streamerName || "Your stream"}
          resultsCount={endedLiveSession.clips?.length || 0}
          onClose={() => { dismissedEndedIdRef.current = String(endedLiveSession._id); setEndedLiveSession(null); }}
          onViewClips={() => { dismissedEndedIdRef.current = String(endedLiveSession._id); setEndedLiveSession(null); setActiveTab("library"); }}
          onUpgrade={() => { dismissedEndedIdRef.current = String(endedLiveSession._id); setEndedLiveSession(null); openUpgrade(); }}
        />
      )}

      {/* Live clipping popup (Pro feature) — triggered by pasting a live Twitch link */}
      <LiveClippingModal
        open={liveModalOpen}
        url={liveModalUrl}
        sessionId={null}
        userPlan={userPlan}
        aspectRatio={aspectRatio}
        burnHookTitle={effectiveBurnHookTitle}
        burnSubtitles={effectiveBurnSubtitles}
        burnWatermark={burnWatermark}
        twitchConn={twitchConn}
        onTwitchDisconnect={() => setTwitchConn(null)}
        kickConn={kickConn}
        onKickDisconnect={() => setKickConn(null)}
        youtubeConn={youtubeConn}
        onYoutubeDisconnect={() => setYoutubeConn(null)}
        onUpgrade={openUpgrade}
        onNotLive={handleLiveNotLive}
        onClose={() => { setLiveModalOpen(false); refreshActiveLive(); }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteJob}
        jobTitle={
          bulkDeleteTargets
            ? t('clipsSelected', { count: bulkDeleteTargets.length })
            : jobToDelete ? (getVideoTitle(jobToDelete) || jobToDelete.streamerName) : ""
        }
        isProcessing={!bulkDeleteTargets && (jobToDelete?.status === "queued" || jobToDelete?.status === "processing")}
      />

      {/* Profile picture update prompt */}
      <ProfilePictureModal
        isOpen={showProfilePicModal}
        onClose={() => setShowProfilePicModal(false)}
      />

      {/* Russell Brand Affiliate Offer */}
      <RussellOfferModal
        isOpen={showRussellOffer}
        onClose={() => setShowRussellOffer(false)}
      />

      <OwnWelcomeModal
        isOpen={ownWelcome.open}
        onClose={() => setOwnWelcome({ open: false })}
        onCta={() => { setOwnWelcome({ open: false }); setShowUpgradeModal(true); }}
      />

      {/* Limit Reached Modal (paid users only) */}
      {showLimitModal && (
        <div className="limit-modal-overlay" onClick={() => setShowLimitModal(false)}>
          <div className="limit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="limit-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff6a00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="limit-modal-title">{t('uploadLimitReached')}</h3>
            <p className="limit-modal-message">{limitMessage}</p>
            {limitResetDate && (
              <p className="limit-modal-reset">
                {t('limitResets', { date: limitResetDate.toLocaleDateString(undefined, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  }) })}
              </p>
            )}
            <button
              className="limit-modal-btn"
              onClick={() => setShowLimitModal(false)}
            >
              {t('gotIt')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForYouPage;